import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from 'react-leaflet';
import { LatLngTuple, LatLngBounds } from 'leaflet';
import { divIcon } from 'leaflet';
import { motion, AnimatePresence } from 'framer-motion';
import { createPortal } from 'react-dom';
import {
  Coffee,
  X,
  DollarSign,
  Search,
  List,
  ArrowLeft,
  MapPin,
  ArrowUp,
  ArrowDown,
  Navigation,
  Clock,
  Star,
  HelpCircle,
  ChevronDown,
  Milk,
} from 'lucide-react';
import MarkerClusterGroup from 'react-leaflet-cluster';
import CoffeeSearch from './CoffeeSearch';
import { calculateDistance, formatDistance } from '../utils/distance';
import { eventLogger, getData } from '../utils/auth';
import {
  preprocessLocation,
  locationHasType,
  locationHasCategory,
  getTypeLabel,
  getCategoryLabel,
  getPopularTypeIds,
  getCategories,
  getTypesForCategory,
  Category,
  Type,
} from '../utils/coffeeFilterMatcherV4';
import { preprocessLocationSizes, NormalizedSize } from '../utils/sizeNormalizer';
import { updateThemeColor } from '../utils/themeColor';
import AppShell from './layout/AppShell';
import { HeaderConfig } from './layout/AppHeader';
import './CoffeeList.css';
import './CoffeeMapper2.css';
import '../styles/App.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.css';
import 'react-leaflet-cluster/dist/assets/MarkerCluster.Default.css';

interface CoffeeCafe {
  name: string;
  latitude: number;
  longitude: number;
  website: string;
  coffeeHotDrinks?: any[];
  icedCoffeeTea?: any[];
}

interface CoffeeMapperProps {
  onClose?: () => void;
  referenceLocation?: [number, number] | null; // Location from Home2 (selected city or current location)
  userLocation?: [number, number] | null; // App-wide current user location
  onRequestLocation?: () => Promise<[number, number]>; // Trigger app location prompt
  isLocating?: boolean; // App-wide locating state
  allCoffeeLocations?: Record<string, any>; // Coffee locations loaded from App.tsx on app load
  preprocessedCoffeeLocations?: Record<string, any>; // Preprocessed coffee locations (type/category/size normalized) from App.tsx
  locationToOpen?: any | null; // Location to automatically open in modal (from Home2 coffee card click)
  onLocationOpened?: () => void; // Callback to clear locationToOpen in App.tsx after modal is opened
  activeTab?: string; // Active tab for AppShell
  onTabChange?: (tab: string) => void; // Tab change handler for AppShell
  onMyVibeClick?: () => void; // My Vibe click handler
  hideNavbar?: boolean; // Hide navbar for native app
}

type FilterType = 'coffee' | 'size' | 'milk' | 'price' | 'rating' | null;

const ITEMS_PER_PAGE = 20; // Number of items to load per batch

// MapController component to set mapRef (matches App.tsx pattern)
const MapController: React.FC<{
  mapRef: React.MutableRefObject<any>;
}> = ({ mapRef }) => {
  const map = useMap();

  // Store map reference for external control
  useEffect(() => {
    if (map && mapRef) {
      mapRef.current = map;
    }
  }, [map, mapRef]);

  return null;
};

// Component to handle zoom level changes
const ZoomHandler: React.FC<{ onZoomChange: (zoom: number) => void }> = ({ onZoomChange }) => {
  const map = useMap();

  useEffect(() => {
    const handleZoom = () => {
      onZoomChange(map.getZoom());
    };

    // Set initial zoom
    onZoomChange(map.getZoom());

    map.on('zoomend', handleZoom);
    return () => {
      map.off('zoomend', handleZoom);
    };
  }, [map, onZoomChange]);

  return null;
};

// Helper function to get price from a location for a specific item name (handles both coffee_menu and legacy formats)
const getLocationPriceByName = (location: any, itemName: string, size: NormalizedSize): number | null => {
  // Try coffee_menu format first (new format)
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    const menuItem = location.coffee_menu[itemName];
    if (menuItem) {
      // Debug: log first item to see structure
      const isFirstItem = Object.keys(location.coffee_menu)[0] === itemName;
      if (isFirstItem) {
        // console.log('[getLocationPriceByName] First menu item:', {
        //   itemName,
        //   menuItem,
        //   hasSizes: !!menuItem.Sizes,
        //   sizeKeys: menuItem.Sizes ? Object.keys(menuItem.Sizes) : 'no Sizes',
        //   directKeys: Object.keys(menuItem),
        //   requestedSize: size,
        // });
      }

      // If menuItem is a direct number, return it
      if (typeof menuItem === 'number') {
        return menuItem;
      }

      // If menuItem is an object, look for Sizes property first
      if (typeof menuItem === 'object' && menuItem !== null) {
        // New format: sizes are under menuItem.Sizes
        const sizesObj = menuItem.Sizes || menuItem; // Fallback to menuItem for old format

        // Check for size keys (small, large, Small, Large, regular, Regular, etc.)
        // Try exact match first
        if (sizesObj[size] !== undefined && typeof sizesObj[size] === 'number') {
          return sizesObj[size];
        }

        // Try case-insensitive match
        const sizeKey = Object.keys(sizesObj).find((k) => k.toLowerCase() === size.toLowerCase());
        if (sizeKey && typeof sizesObj[sizeKey] === 'number') {
          return sizesObj[sizeKey];
        }

        // Try alternative size names for regular (if regular not found, try standard, medium, etc.)
        if (size === 'regular') {
          const altKeys = ['standard', 'Standard', 'STANDARD', 'medium', 'Medium', 'MEDIUM', 'med', 'Med', 'MED'];
          for (const altKey of altKeys) {
            if (sizesObj[altKey] !== undefined && typeof sizesObj[altKey] === 'number') {
              return sizesObj[altKey];
            }
          }
        }

        // For small, try regular as fallback (smart fallback)
        if (size === 'small') {
          if (sizesObj['regular'] !== undefined && typeof sizesObj['regular'] === 'number') {
            return sizesObj['regular'];
          }
        }

        // If no size-specific key found, try to get any numeric value
        const numericValues = Object.values(sizesObj).filter((v: any) => typeof v === 'number') as number[];
        if (numericValues.length > 0) {
          return numericValues[0];
        }
      }
    }
  }

  // Fallback to legacy format (coffeeHotDrinks/icedCoffeeTea arrays)
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    const drink = location.coffeeHotDrinks.find((d: any) => d.name === itemName);
    if (drink && drink.price) {
      // Try exact size match
      if (drink.price[size] !== undefined && typeof drink.price[size] === 'number') {
        return drink.price[size];
      }
      // Try case-insensitive match
      const sizeKey = Object.keys(drink.price).find((k) => k.toLowerCase() === size.toLowerCase());
      if (sizeKey && typeof drink.price[sizeKey] === 'number') {
        return drink.price[sizeKey];
      }
      // Smart fallback: for regular, try medium/standard; for small, try regular
      if (size === 'regular') {
        const altKeys = ['standard', 'Standard', 'STANDARD', 'medium', 'Medium', 'MEDIUM', 'med', 'Med', 'MED'];
        for (const altKey of altKeys) {
          if (drink.price[altKey] !== undefined && typeof drink.price[altKey] === 'number') {
            return drink.price[altKey];
          }
        }
      } else if (size === 'small' && drink.price.regular !== undefined && typeof drink.price.regular === 'number') {
        return drink.price.regular;
      }
      // If price is a direct number
      if (typeof drink.price === 'number') {
        return drink.price;
      }
    }
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    const drink = location.icedCoffeeTea.find((d: any) => d.name === itemName);
    if (drink && drink.price) {
      // Try exact size match
      if (drink.price[size] !== undefined && typeof drink.price[size] === 'number') {
        return drink.price[size];
      }
      // Try case-insensitive match
      const sizeKey = Object.keys(drink.price).find((k) => k.toLowerCase() === size.toLowerCase());
      if (sizeKey && typeof drink.price[sizeKey] === 'number') {
        return drink.price[sizeKey];
      }
      // Smart fallback: for regular, try medium/standard; for small, try regular
      if (size === 'regular') {
        const altKeys = ['standard', 'Standard', 'STANDARD', 'medium', 'Medium', 'MEDIUM', 'med', 'Med', 'MED'];
        for (const altKey of altKeys) {
          if (drink.price[altKey] !== undefined && typeof drink.price[altKey] === 'number') {
            return drink.price[altKey];
          }
        }
      } else if (size === 'small' && drink.price.regular !== undefined && typeof drink.price.regular === 'number') {
        return drink.price.regular;
      }
      // If price is a direct number
      if (typeof drink.price === 'number') {
        return drink.price;
      }
    }
  }

  return null;
};

// Helper function to extract any price from a menu item (for aggressive fallback)
const extractAnyPriceFromMenuItem = (menuItem: any): number | null => {
  if (typeof menuItem === 'number') {
    return menuItem;
  }
  if (typeof menuItem === 'object' && menuItem !== null) {
    // New format: check Sizes property first
    const sizesObj = menuItem.Sizes || menuItem;

    // Try to find any numeric value in the sizes object
    const numericValues = Object.values(sizesObj).filter((v: any) => typeof v === 'number') as number[];
    if (numericValues.length > 0) {
      return numericValues[0]; // Return first found price
    }
  }
  return null;
};

// Helper function to get price from a location for a type ID (using preprocessed data)
// Returns the most common (mode) price among all matching items
// Falls back to other size, then any available price if selected size doesn't have a price
const getLocationPriceByTypeId = (location: any, typeId: string, size: NormalizedSize): number | null => {
  // Get all items from the location with prices for the selected size
  const items: Array<{ name: string; price: number | null }> = [];

  // Try coffee_menu format first (new format) - use preprocessed typeId
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
      // Check if this item has the matching typeId (from preprocessing - can be _typeId or typeId)
      if (menuItem && typeof menuItem === 'object' && (menuItem._typeId === typeId || menuItem.typeId === typeId)) {
        // Extract price from menuItem.Sizes
        let price: number | null = null;
        const sizesObj = menuItem.Sizes || menuItem; // Support both new and old format

        // Try to get price for the selected size
        if (sizesObj[size] !== undefined && typeof sizesObj[size] === 'number') {
          price = sizesObj[size];
        } else {
          // Try case-insensitive match
          const sizeKey = Object.keys(sizesObj).find(
            (k) => k.toLowerCase() === size.toLowerCase() && typeof sizesObj[k] === 'number'
          );
          if (sizeKey) {
            price = sizesObj[sizeKey];
          } else if (sizesObj.default !== undefined && typeof sizesObj.default === 'number') {
            price = sizesObj.default;
          } else {
            // Try alternative size names for regular (if regular not found, try standard, medium, etc.)
            if (size === 'regular') {
              const altKeys = ['standard', 'Standard', 'STANDARD', 'medium', 'Medium', 'MEDIUM', 'med', 'Med', 'MED'];
              for (const altKey of altKeys) {
                if (sizesObj[altKey] !== undefined && typeof sizesObj[altKey] === 'number') {
                  price = sizesObj[altKey];
                  break;
                }
              }
            }

            // For small, try regular as fallback (smart fallback)
            if (size === 'small' && price === null) {
              if (sizesObj['regular'] !== undefined && typeof sizesObj['regular'] === 'number') {
                price = sizesObj['regular'];
              }
            }

            // If still no price, try to get any numeric value (excluding metadata)
            if (price === null) {
              const numericValues = Object.entries(sizesObj)
                .filter(
                  ([key, value]: [string, any]) =>
                    key !== '_typeId' &&
                    key !== '_categoryId' &&
                    key !== 'typeId' &&
                    key !== 'categoryId' &&
                    typeof value === 'number'
                )
                .map(([key, value]: [string, any]) => value as number);
              if (numericValues.length > 0) {
                price = numericValues[0];
              }
            }
          }
        }

        if (price !== null) {
          items.push({ name: itemName, price });
        }
      }
    });
  }

  // Fallback to legacy format (coffeeHotDrinks/icedCoffeeTea arrays) - use preprocessed typeId
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        const price = getLocationPriceByName(location, drink.name, size);
        if (price !== null) {
          items.push({ name: drink.name, price });
        }
      }
    });
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        const price = getLocationPriceByName(location, drink.name, size);
        if (price !== null) {
          items.push({ name: drink.name, price });
        }
      }
    });
  }

  // If we found prices for the selected size, calculate the most common one
  if (items.length > 0) {
    // Calculate the most common (mode) price
    // Round prices to 2 decimal places for comparison (to handle floating point precision)
    const priceCounts = new Map<number, number>();
    items.forEach((item) => {
      if (item.price !== null) {
        const roundedPrice = Math.round(item.price * 100) / 100; // Round to 2 decimals
        priceCounts.set(roundedPrice, (priceCounts.get(roundedPrice) || 0) + 1);
      }
    });

    // Find the price with the highest count
    let maxCount = 0;
    priceCounts.forEach((count) => {
      if (count > maxCount) {
        maxCount = count;
      }
    });

    // If multiple prices have the same count, return the cheapest one
    const pricesWithMaxCount: number[] = [];
    priceCounts.forEach((count, price) => {
      if (count === maxCount) {
        pricesWithMaxCount.push(price);
      }
    });

    if (pricesWithMaxCount.length > 0) {
      return Math.min(...pricesWithMaxCount);
    }
  }

  // Fallback: if no price found for selected size, try the other size
  // Determine fallback size based on filter behavior
  // For 'regular': smart fallback (try small if only small+large exist, or large)
  // For 'small' or 'large': no fallback (return null if not found)
  let fallbackSize: NormalizedSize | null = null;
  if (size === 'regular') {
    // Smart fallback: try small first (if only small+large exist, small is treated as regular)
    // Then try large
    fallbackSize = 'small';
  } else if (size === 'small') {
    // No fallback for small
    fallbackSize = null;
  } else {
    // No fallback for large
    fallbackSize = null;
  }
  const fallbackItems: Array<{ name: string; price: number | null }> = [];

  // Try coffee_menu format first (new format) - use preprocessed typeId
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
      if (menuItem && typeof menuItem === 'object' && (menuItem._typeId === typeId || menuItem.typeId === typeId)) {
        // Extract price from menuItem.Sizes for the fallback size
        let price: number | null = null;
        const sizesObj = menuItem.Sizes || menuItem; // Support both new and old format

        if (fallbackSize && sizesObj[fallbackSize] !== undefined && typeof sizesObj[fallbackSize] === 'number') {
          price = sizesObj[fallbackSize];
        } else if (fallbackSize) {
          const sizeKey = Object.keys(sizesObj).find(
            (k) => k.toLowerCase() === fallbackSize.toLowerCase() && typeof sizesObj[k] === 'number'
          );
          if (sizeKey) {
            price = sizesObj[sizeKey];
          } else if (sizesObj.default !== undefined && typeof sizesObj.default === 'number') {
            price = sizesObj.default;
          } else {
            const numericValues = Object.values(sizesObj).filter(
              (v: any) => typeof v === 'number' && v !== menuItem._typeId && v !== menuItem._categoryId
            ) as number[];
            if (numericValues.length > 0) {
              price = numericValues[0];
            }
          }
        }

        if (price !== null) {
          fallbackItems.push({ name: itemName, price });
        }
      }
    });
  }

  // Fallback to legacy format (coffeeHotDrinks/icedCoffeeTea arrays) - use preprocessed typeId
  if (fallbackSize && location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        const price = getLocationPriceByName(location, drink.name, fallbackSize);
        if (price !== null) {
          fallbackItems.push({ name: drink.name, price });
        }
      }
    });
  }

  if (fallbackSize && location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        const price = getLocationPriceByName(location, drink.name, fallbackSize);
        if (price !== null) {
          fallbackItems.push({ name: drink.name, price });
        }
      }
    });
  }

  // If we found fallback prices, return the most common one
  if (fallbackItems.length > 0) {
    const priceCounts = new Map<number, number>();
    fallbackItems.forEach((item) => {
      if (item.price !== null) {
        const roundedPrice = Math.round(item.price * 100) / 100;
        priceCounts.set(roundedPrice, (priceCounts.get(roundedPrice) || 0) + 1);
      }
    });

    let maxCount = 0;
    priceCounts.forEach((count) => {
      if (count > maxCount) {
        maxCount = count;
      }
    });

    const pricesWithMaxCount: number[] = [];
    priceCounts.forEach((count, price) => {
      if (count === maxCount) {
        pricesWithMaxCount.push(price);
      }
    });

    if (pricesWithMaxCount.length > 0) {
      return Math.min(...pricesWithMaxCount);
    }
  }

  // Final fallback: try to extract ANY price from matching items (regardless of size)
  const anyPriceItems: Array<{ name: string; price: number | null }> = [];

  // Try coffee_menu format - use preprocessed typeId
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
      if (menuItem && typeof menuItem === 'object' && (menuItem._typeId === typeId || menuItem.typeId === typeId)) {
        // Extract any price from the menuItem (skip _typeId and _categoryId metadata)
        const price = extractAnyPriceFromMenuItem(menuItem);
        if (price !== null) {
          anyPriceItems.push({ name: itemName, price });
        }
      }
    });
  }

  // Try legacy format - use preprocessed typeId
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        let price: number | null = null;
        if (drink.price) {
          if (typeof drink.price === 'number') {
            price = drink.price;
          } else if (typeof drink.price === 'object') {
            // Try to get any numeric value from price object
            const numericValues = Object.values(drink.price).filter((v: any) => typeof v === 'number') as number[];
            if (numericValues.length > 0) {
              price = numericValues[0];
            }
          }
        }
        if (price !== null) {
          anyPriceItems.push({ name: drink.name, price });
        }
      }
    });
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        let price: number | null = null;
        if (drink.price) {
          if (typeof drink.price === 'number') {
            price = drink.price;
          } else if (typeof drink.price === 'object') {
            // Try to get any numeric value from price object
            const numericValues = Object.values(drink.price).filter((v: any) => typeof v === 'number') as number[];
            if (numericValues.length > 0) {
              price = numericValues[0];
            }
          }
        }
        if (price !== null) {
          anyPriceItems.push({ name: drink.name, price });
        }
      }
    });
  }

  // If we found any prices, return the most common one
  if (anyPriceItems.length > 0) {
    const priceCounts = new Map<number, number>();
    anyPriceItems.forEach((item) => {
      if (item.price !== null) {
        const roundedPrice = Math.round(item.price * 100) / 100;
        priceCounts.set(roundedPrice, (priceCounts.get(roundedPrice) || 0) + 1);
      }
    });

    let maxCount = 0;
    priceCounts.forEach((count) => {
      if (count > maxCount) {
        maxCount = count;
      }
    });

    const pricesWithMaxCount: number[] = [];
    priceCounts.forEach((count, price) => {
      if (count === maxCount) {
        pricesWithMaxCount.push(price);
      }
    });

    if (pricesWithMaxCount.length > 0) {
      return Math.min(...pricesWithMaxCount);
    }
  }

  return null;
};

// Legacy function name for compatibility - now uses typeId
const getLocationPriceByFilter = (location: any, typeId: string, size: NormalizedSize): number | null => {
  return getLocationPriceByTypeId(location, typeId, size);
};

// Helper function to get the cheapest price across all sizes for a given type
// Used when no size preference is explicitly selected
const getCheapestPriceByTypeId = (location: any, typeId: string): number | null => {
  const prices: number[] = [];

  // Try coffee_menu format first (new format)
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
      if (menuItem && typeof menuItem === 'object' && (menuItem._typeId === typeId || menuItem.typeId === typeId)) {
        const sizesObj = menuItem.Sizes || menuItem;

        // Extract all numeric prices from all sizes
        Object.entries(sizesObj).forEach(([key, value]: [string, any]) => {
          if (
            typeof value === 'number' &&
            key !== '_typeId' &&
            key !== '_categoryId' &&
            key !== 'typeId' &&
            key !== 'categoryId'
          ) {
            prices.push(value);
          }
        });
      }
    });
  }

  // Fallback to legacy format
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId && drink.price) {
        Object.values(drink.price).forEach((value: any) => {
          if (typeof value === 'number') {
            prices.push(value);
          }
        });
      }
    });
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId && drink.price) {
        Object.values(drink.price).forEach((value: any) => {
          if (typeof value === 'number') {
            prices.push(value);
          }
        });
      }
    });
  }

  if (prices.length === 0) return null;
  return Math.min(...prices);
};

// Helper function to check if a location has a specific milk option (case-insensitive)
// If activeTypeId is provided, only check items matching that type
const locationHasMilkOption = (location: any, milkOption: string, activeTypeId?: string | null): boolean => {
  if (!milkOption) return true; // No filter, so all locations match

  const normalizedMilkOption = milkOption.toLowerCase();

  // Check coffee_menu format first
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    for (const menuItem of Object.values(location.coffee_menu)) {
      if (menuItem && typeof menuItem === 'object') {
        // If activeTypeId is provided, only check items matching that type
        if (activeTypeId) {
          const matchesType = (menuItem as any)._typeId === activeTypeId || (menuItem as any).typeId === activeTypeId;
          if (!matchesType) {
            continue; // Skip items that don't match the type filter
          }
        }

        // Now check if this item has the milk option
        if ((menuItem as any).Milks) {
          const milkKeys = Object.keys((menuItem as any).Milks);
          if (milkKeys.some((key) => key.toLowerCase() === normalizedMilkOption)) {
            return true;
          }
        }
      }
    }
  }

  // Check legacy format
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    for (const drink of location.coffeeHotDrinks) {
      // If activeTypeId is provided, only check drinks matching that type
      if (activeTypeId && drink.typeId !== activeTypeId) {
        continue; // Skip drinks that don't match the type filter
      }

      if (drink.milks && typeof drink.milks === 'object') {
        const milkKeys = Object.keys(drink.milks);
        if (milkKeys.some((key) => key.toLowerCase() === normalizedMilkOption)) {
          return true;
        }
      }
    }
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    for (const drink of location.icedCoffeeTea) {
      // If activeTypeId is provided, only check drinks matching that type
      if (activeTypeId && drink.typeId !== activeTypeId) {
        continue; // Skip drinks that don't match the type filter
      }

      if (drink.milks && typeof drink.milks === 'object') {
        const milkKeys = Object.keys(drink.milks);
        if (milkKeys.some((key) => key.toLowerCase() === normalizedMilkOption)) {
          return true;
        }
      }
    }
  }

  return false;
};

// Helper function to get milk surcharge for a specific item
const getMilkSurcharge = (location: any, itemName: string, milkOption: string): number => {
  if (!milkOption) return 0;

  const normalizedMilkOption = milkOption.toLowerCase();

  // Try coffee_menu format first (new format)
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    const menuItem = location.coffee_menu[itemName];
    if (menuItem && typeof menuItem === 'object' && menuItem.Milks) {
      const milkKeys = Object.keys(menuItem.Milks);
      const matchingKey = milkKeys.find((key) => key.toLowerCase() === normalizedMilkOption);
      if (matchingKey && typeof menuItem.Milks[matchingKey] === 'number') {
        return menuItem.Milks[matchingKey];
      }
    }
  }

  // Fallback to legacy format (coffeeHotDrinks/icedCoffeeTea arrays)
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    const drink = location.coffeeHotDrinks.find((d: any) => d.name === itemName);
    if (drink && drink.milks && typeof drink.milks === 'object') {
      const milkKeys = Object.keys(drink.milks);
      const matchingKey = milkKeys.find((key) => key.toLowerCase() === normalizedMilkOption);
      if (matchingKey && typeof drink.milks[matchingKey] === 'number') {
        return drink.milks[matchingKey];
      }
    }
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    const drink = location.icedCoffeeTea.find((d: any) => d.name === itemName);
    if (drink && drink.milks && typeof drink.milks === 'object') {
      const milkKeys = Object.keys(drink.milks);
      const matchingKey = milkKeys.find((key) => key.toLowerCase() === normalizedMilkOption);
      if (matchingKey && typeof drink.milks[matchingKey] === 'number') {
        return drink.milks[matchingKey];
      }
    }
  }

  return 0;
};

// Helper function to get milk surcharge by typeId (averages all matching items)
const getMilkSurchargeByTypeId = (location: any, typeId: string, milkOption: string): number => {
  if (!milkOption) return 0;

  const surcharges: number[] = [];

  // Try coffee_menu format first (new format)
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.entries(location.coffee_menu).forEach(([itemName, menuItem]: [string, any]) => {
      if (menuItem && typeof menuItem === 'object' && (menuItem._typeId === typeId || menuItem.typeId === typeId)) {
        const surcharge = getMilkSurcharge(location, itemName, milkOption);
        if (surcharge > 0) {
          surcharges.push(surcharge);
        }
      }
    });
  }

  // Fallback to legacy format
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        const surcharge = getMilkSurcharge(location, drink.name, milkOption);
        if (surcharge > 0) {
          surcharges.push(surcharge);
        }
      }
    });
  }

  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name && drink.typeId === typeId) {
        const surcharge = getMilkSurcharge(location, drink.name, milkOption);
        if (surcharge > 0) {
          surcharges.push(surcharge);
        }
      }
    });
  }

  // Return the most common (mode) surcharge, or 0 if none found
  if (surcharges.length === 0) return 0;

  // Calculate the most common surcharge
  const surchargeCounts = new Map<number, number>();
  surcharges.forEach((surcharge) => {
    const rounded = Math.round(surcharge * 100) / 100;
    surchargeCounts.set(rounded, (surchargeCounts.get(rounded) || 0) + 1);
  });

  let maxCount = 0;
  let mostCommonSurcharge = 0;
  surchargeCounts.forEach((count, surcharge) => {
    if (count > maxCount) {
      maxCount = count;
      mostCommonSurcharge = surcharge;
    }
  });

  return mostCommonSurcharge;
};

// Helper function to extract coffee types from a location
const extractCoffeeTypesFromLocation = (location: any): { hot: string[]; iced: string[]; prices: number[] } => {
  const hot: string[] = [];
  const iced: string[] = [];
  const prices: number[] = [];

  // Try coffee_menu format first
  if (location.coffee_menu && typeof location.coffee_menu === 'object') {
    Object.keys(location.coffee_menu).forEach((itemName) => {
      const menuItem = location.coffee_menu[itemName];
      hot.push(itemName); // Assume all items in coffee_menu are hot drinks

      if (typeof menuItem === 'object' && menuItem !== null) {
        // Access Sizes property for new format
        const sizesObj = menuItem.Sizes || menuItem;
        Object.values(sizesObj).forEach((price: any) => {
          if (typeof price === 'number') {
            prices.push(price);
          }
        });
      } else if (typeof menuItem === 'number') {
        prices.push(menuItem);
      }
    });
  }

  // Fallback to legacy format
  if (location.coffeeHotDrinks && Array.isArray(location.coffeeHotDrinks)) {
    location.coffeeHotDrinks.forEach((drink: any) => {
      if (drink.name) hot.push(drink.name);
      if (drink.price) {
        if (drink.price.small) prices.push(drink.price.small);
        if (drink.price.large) prices.push(drink.price.large);
      }
    });
  }
  if (location.icedCoffeeTea && Array.isArray(location.icedCoffeeTea)) {
    location.icedCoffeeTea.forEach((drink: any) => {
      if (drink.name) iced.push(drink.name);
      if (drink.price) {
        if (drink.price.small) prices.push(drink.price.small);
        if (drink.price.large) prices.push(drink.price.large);
      }
    });
  }

  return { hot, iced, prices };
};

// Calculate quartile boundaries from sorted prices
const calculateQuartiles = (prices: number[]): { q25: number; q50: number; q75: number } => {
  if (prices.length === 0) return { q25: 0, q50: 0, q75: 0 };

  const sorted = [...prices].sort((a, b) => a - b);
  const len = sorted.length;

  const q25Index = Math.floor(len * 0.25);
  const q50Index = Math.floor(len * 0.5);
  const q75Index = Math.floor(len * 0.75);

  return {
    q25: sorted[q25Index] || sorted[0],
    q50: sorted[q50Index] || sorted[Math.floor(len / 2)],
    q75: sorted[q75Index] || sorted[len - 1],
  };
};

// Get price color based on quartiles: Green (bottom 25%), Yellow (25-50%), Orange (50-75%), Red (top 25%)
const getPriceColor = (price: number, prices: number[]): string => {
  if (prices.length === 0) return '#7c3aed'; // Default color if no prices

  if (prices.length === 1) return '#4CAF50'; // All same price, use green

  const { q25, q50, q75 } = calculateQuartiles(prices);

  // Green: bottom 25% (cheapest) - price <= q25
  if (price <= q25) return '#4CAF50';
  // Yellow: 25-50% - q25 < price <= q50
  else if (price <= q50) return '#FFC107';
  // Orange: 50-75% - q50 < price <= q75
  else if (price <= q75) return '#FF9800';
  // Red: top 25% (most expensive) - price > q75
  else return '#F44336';
};

const CoffeeMapper2: React.FC<CoffeeMapperProps> = ({
  onClose,
  referenceLocation: referenceLocationProp,
  userLocation: userLocationProp,
  onRequestLocation,
  isLocating: isLocatingProp = false,
  allCoffeeLocations: allCoffeeLocationsProp,
  preprocessedCoffeeLocations: preprocessedCoffeeLocationsProp,
  locationToOpen,
  onLocationOpened,
  activeTab = 'coffee',
  onTabChange,
  onMyVibeClick,
  hideNavbar = false,
}) => {
  const [mapCenter, setMapCenter] = useState<LatLngTuple>([-33.8688, 151.2093]); // Sydney CBD
  const [mapZoom, setMapZoom] = useState(15);
  const mapRef = useRef<any>(null);
  const [coffeeMarkers, setCoffeeMarkers] = useState<React.ReactNode[]>([]);
  // Use prop if provided (from App.tsx), otherwise use local state (fallback for standalone usage)
  const [allCoffeeLocationsLocal, setAllCoffeeLocationsLocal] = useState<Record<string, any>>({});
  const allCoffeeLocations = allCoffeeLocationsProp || allCoffeeLocationsLocal;
  const [coffeeLocationMarkers, setCoffeeLocationMarkers] = useState<React.ReactNode[]>([]);
  const [selectedCoffeeLocation, setSelectedCoffeeLocation] = useState<any>(null);
  const [userLocationLocal, setUserLocationLocal] = useState<LatLngTuple | null>(null);
  const [userLocationMarker, setUserLocationMarker] = useState<React.ReactNode | null>(null);
  // App-wide location takes precedence over local
  const userLocation = userLocationProp ?? userLocationLocal;

  // Use preprocessed locations from prop if provided, otherwise use local state
  const [preprocessedLocationsLocal, setPreprocessedLocationsLocal] = useState<Record<string, any>>({});
  const preprocessedLocations = preprocessedCoffeeLocationsProp || preprocessedLocationsLocal;

  // Load Home2 location from localStorage as fallback
  const [home2LocationFromStorage, setHome2LocationFromStorage] = useState<[number, number] | null>(null);

  // Use referenceLocation prop if provided, otherwise try localStorage, otherwise null
  const referenceLocation = referenceLocationProp || home2LocationFromStorage || null;

  // Load Home2 location from localStorage on mount (fallback if prop not provided)
  useEffect(() => {
    if (referenceLocationProp) return; // If prop is provided, don't load from storage

    try {
      const stored = localStorage.getItem('vibecheck_home2_location');
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.useCurrentLocation && parsed.coords) {
          setHome2LocationFromStorage([parsed.coords[0], parsed.coords[1]]);
        } else if (parsed.cityName) {
          // City coordinates lookup
          const cityCoords: Record<string, [number, number]> = {
            Sydney: [-33.8688, 151.2093],
            Melbourne: [-37.8136, 144.9631],
            Brisbane: [-27.4698, 153.0251],
            Perth: [-31.9505, 115.8605],
            Adelaide: [-34.9285, 138.6007],
            Canberra: [-35.2809, 149.13],
          };
          const coords = cityCoords[parsed.cityName];
          if (coords) {
            setHome2LocationFromStorage(coords);
          }
        }
      }
    } catch (e) {
      console.error('Error loading Home2 location from localStorage:', e);
    }
  }, [referenceLocationProp]);

  // Update theme-color for iOS Safari
  useEffect(() => {
    updateThemeColor('#7c3aed'); // Purple to match coffee mapper theme
    return () => {
      updateThemeColor('#ffffff'); // Restore white when unmounts
    };
  }, []);

  // Performance optimization refs (matches App.tsx pattern)
  const iconCacheRef = useRef<any>(null);
  const isMountedRef = useRef(true);

  // Prevent duplicate Firebase calls during clustering/marker interactions (matches App.tsx pattern)
  const coffeeLocationDetailsCacheRef = useRef<Map<string, any>>(new Map());
  const pendingCoffeeLocationRequestsRef = useRef<Set<string>>(new Set());

  // Ref to store handleCoffeeLocationClick so it can be accessed in createCoffeeMarkers
  const handleCoffeeLocationClickRef = useRef<((location: any, shop: any, details: any) => Promise<void>) | null>(null);

  const [isLocatingLocal, setIsLocatingLocal] = useState(false);
  const isLocating = isLocatingProp || isLocatingLocal;
  const [activeFilter, setActiveFilter] = useState<FilterType>(null);

  // Popular cafes list
  const POPULAR_CAFES = [
    'Skittle Lane',
    'Edition Roasters MidCity',
    'Gumption by Coffee Alchemy',
    'Regiment Cafe',
    'Sevens Specialty Coffee CBD',
    'Leible Coffee Pitt St',
    "Ek'sentrik Cafe Kent Street",
    "Diggy Doo's",
  ];

  // Static list of milk options for filtering
  const MILK_OPTIONS = ['Full cream', 'Lactose Free', 'Soy', 'Skim', 'Almond', 'Oat'];

  const [showPopularOnly, setShowPopularOnly] = useState(false);

  // Load persisted coffee type filter from localStorage
  const loadPersistedCoffeeType = (): string | null => {
    try {
      const stored = localStorage.getItem('vibecheck_coffeemapper_type_filter');
      if (stored) {
        return stored;
      }
    } catch (e) {
      console.error('Error loading persisted coffee type filter:', e);
    }
    return 'type_cappuccino_hot'; // Default value
  };

  // 3-level filtering state
  const [mode, setMode] = useState<'popular' | 'categories'>('popular');
  const [temperatureFilter, setTemperatureFilter] = useState<'hot' | 'cold' | null>(null);
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeTypeId, setActiveTypeId] = useState<string | null>(loadPersistedCoffeeType());

  const [selectedSize, setSelectedSize] = useState<NormalizedSize>('regular');
  const [sizeExplicitlySelected, setSizeExplicitlySelected] = useState<boolean>(false); // Track if user explicitly selected size
  const [selectedMilk, setSelectedMilk] = useState<string | null>(null);
  const [selectedCafe, setSelectedCafe] = useState<CoffeeCafe | null>(null);
  const [priceRange, setPriceRange] = useState<{ min: number; max: number } | null>(null);
  const [sliderMin, setSliderMin] = useState<number>(3);
  const [sliderMax, setSliderMax] = useState<number>(15);
  const [minRating, setMinRating] = useState<number | null>(null);

  // Fixed price range for slider
  const SLIDER_PRICE_MIN = 3;
  const SLIDER_PRICE_MAX = 15;
  const [showCoffeeSearch, setShowCoffeeSearch] = useState(false);
  const [showCoffeeList, setShowCoffeeList] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [listSortBy, setListSortBy] = useState<'price' | 'distance' | 'rating'>('distance');
  const [priceSortOrder, setPriceSortOrder] = useState<'lowest' | 'highest'>('lowest');
  const [displayedCount, setDisplayedCount] = useState(ITEMS_PER_PAGE);
  const [currentZoom, setCurrentZoom] = useState<number>(13);
  const [markerData, setMarkerData] = useState([]);
  const observerTarget = useRef<HTMLDivElement>(null);
  const [pinDisplayMode, setPinDisplayMode] = useState<'price' | 'rating'>('price');

  useEffect(() => {
    eventLogger('location_tab', { location: selectedCafe?.name, tab: 'coffee' });
  }, [selectedCafe]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Temperature filter: ${temperatureFilter}`, tab: 'coffee' });
  }, [temperatureFilter]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Category selected: ${activeCategoryId}`, tab: 'coffee' });
  }, [activeCategoryId]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Type selected: ${activeTypeId}`, tab: 'coffee' });
  }, [activeTypeId]);

  // Persist coffee type filter to localStorage
  useEffect(() => {
    try {
      if (activeTypeId) {
        localStorage.setItem('vibecheck_coffeemapper_type_filter', activeTypeId);
      } else {
        localStorage.removeItem('vibecheck_coffeemapper_type_filter');
      }
    } catch (e) {
      console.error('Error persisting coffee type filter:', e);
    }
  }, [activeTypeId]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Price set: ${priceRange}`, tab: 'coffee' });
  }, [priceRange]);

  useEffect(() => {
    eventLogger('location_tab', { location: `List by: ${listSortBy}`, tab: 'coffee' });
  }, [listSortBy]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Help modal clicked`, tab: 'coffee' });
  }, [showHelpModal]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Size selected: ${selectedSize}`, tab: 'coffee' });
  }, [selectedSize]);

  useEffect(() => {
    eventLogger('location_tab', { location: `Mode selected: ${mode}`, tab: 'coffee' });
  }, [mode]);

  // Calculate price range from all locations
  const availablePriceRange = useMemo(() => {
    const prices: number[] = [];

    Object.values(allCoffeeLocations).forEach((location: any) => {
      const { prices: locationPrices } = extractCoffeeTypesFromLocation(location);
      prices.push(...locationPrices);
    });

    const minPrice = prices.length > 0 ? Math.floor(Math.min(...prices) * 10) / 10 : 3;
    const maxPrice = prices.length > 0 ? Math.ceil(Math.max(...prices) * 10) / 10 : 8;

    return { min: minPrice, max: maxPrice };
  }, [allCoffeeLocations]);

  // Function to filter overlapping markers and keep only the cheapest ones
  const filterOverlappingMarkers = useCallback(
    (markerData: Array<{ element: React.ReactNode; price: number | null; lat: number; lng: number }>) => {
      if (!mapRef.current || currentZoom < 15) {
        return markerData.map((m) => m.element);
      }

      const map = mapRef.current;
      const OVERLAP_THRESHOLD_PIXELS = 25; // Markers closer than this are considered overlapping

      // Convert lat/lng to pixel coordinates for overlap detection
      const markersWithPixels = markerData.map((marker) => {
        const point = map.latLngToContainerPoint([marker.lat, marker.lng]);
        return {
          ...marker,
          x: point.x,
          y: point.y,
        };
      });

      // Group overlapping markers
      const groups: Array<Array<(typeof markersWithPixels)[0]>> = [];
      const processed = new Set<number>();

      markersWithPixels.forEach((marker, index) => {
        if (processed.has(index)) return;

        const group = [marker];
        processed.add(index);

        // Find all markers that overlap with this one
        markersWithPixels.forEach((otherMarker, otherIndex) => {
          if (processed.has(otherIndex) || index === otherIndex) return;

          const distance = Math.sqrt(Math.pow(marker.x - otherMarker.x, 2) + Math.pow(marker.y - otherMarker.y, 2));

          if (distance < OVERLAP_THRESHOLD_PIXELS) {
            group.push(otherMarker);
            processed.add(otherIndex);
          }
        });

        groups.push(group);
      });

      // From each group, keep only the marker with the cheapest price
      const filteredMarkers: React.ReactNode[] = [];

      groups.forEach((group) => {
        let cheapestMarker = group[0];

        group.forEach((marker) => {
          // If current cheapest has no price but this one does, prefer this one
          if (cheapestMarker.price === null && marker.price !== null) {
            cheapestMarker = marker;
          }
          // If both have prices, prefer the cheaper one
          else if (cheapestMarker.price !== null && marker.price !== null && marker.price < cheapestMarker.price) {
            cheapestMarker = marker;
          }
          // If neither has price, keep the first one (could use other criteria like popularity)
        });

        filteredMarkers.push(cheapestMarker.element);
      });

      return filteredMarkers;
    },
    [currentZoom]
  );

  // Create coffee shop markers with price-based colors (using allCoffeeLocations)
  const createCoffeeMarkers = useCallback(() => {
    const locations = Object.entries(allCoffeeLocations);

    const filteredLocations = locations.filter(([name, location]: [string, any]) => {
      const lat = location.latitude;
      const lng = location.longitude;
      if (lat === undefined || lat === null || isNaN(lat) || lng === undefined || lng === null || isNaN(lng)) {
        return false;
      }

      // Filter by popular cafes if enabled
      if (showPopularOnly) {
        const isPopular = POPULAR_CAFES.some(
          (popularName) =>
            name.toLowerCase().includes(popularName.toLowerCase()) ||
            popularName.toLowerCase().includes(name.toLowerCase())
        );
        if (!isPopular) {
          return false;
        }
      }

      // Filter by minimum rating if set (apply this independently of coffee type filter)
      if (minRating !== null) {
        const locationRating = location.review_rating;
        if (locationRating === undefined || locationRating === null) {
          return false; // Exclude locations without ratings when filter is active
        }
        const ratingValue = typeof locationRating === 'number' ? locationRating : parseFloat(String(locationRating));

        // "At least" means >=, so exclude if rating is less than minimum
        // Use a small epsilon for floating point comparison
        if (isNaN(ratingValue) || ratingValue < minRating - 0.01) {
          return false;
        }
      }

      if (!activeTypeId) {
        return true;
      }

      // Fast filtering using preprocessed typeId
      const preprocessed = preprocessedLocations[name] || location;
      let hasMatchingType = false;

      // Check coffee_menu format (using _typeId from preprocessing)
      if (preprocessed.coffee_menu && typeof preprocessed.coffee_menu === 'object') {
        hasMatchingType = Object.values(preprocessed.coffee_menu).some(
          (menuItem: any) =>
            menuItem &&
            typeof menuItem === 'object' &&
            (menuItem._typeId === activeTypeId || menuItem.typeId === activeTypeId)
        );
      }

      // Check legacy format
      if (!hasMatchingType && preprocessed.coffeeHotDrinks && Array.isArray(preprocessed.coffeeHotDrinks)) {
        hasMatchingType = preprocessed.coffeeHotDrinks.some((drink: any) => drink.typeId === activeTypeId);
      }

      if (!hasMatchingType && preprocessed.icedCoffeeTea && Array.isArray(preprocessed.icedCoffeeTea)) {
        hasMatchingType = preprocessed.icedCoffeeTea.some((drink: any) => drink.typeId === activeTypeId);
      }

      if (!hasMatchingType) {
        return false;
      }

      // Check price if price range filter is set
      if (priceRange) {
        const price = getLocationPriceByFilter(preprocessed, activeTypeId, selectedSize);
        if (price === null || price < priceRange.min || price > priceRange.max) {
          return false;
        }
      }

      // Exclude prices < $3
      if (activeTypeId) {
        const price = getLocationPriceByFilter(preprocessed, activeTypeId, selectedSize);
        if (price !== null && price < 3) {
          return false;
        }
      }

      // Filter by milk option if selected (only for items matching the active type)
      if (selectedMilk && !locationHasMilkOption(preprocessed, selectedMilk, activeTypeId)) {
        return false;
      }

      return true;
    });

    const hasFilters = activeTypeId !== null || selectedMilk !== null || minRating !== null || showPopularOnly;

    // Calculate all filtered prices for quartile-based coloring
    const filteredPrices: number[] = [];
    if (hasFilters && filteredLocations.length > 0 && activeTypeId) {
      filteredLocations.forEach(([name, location]: [string, any]) => {
        // Use preprocessed location for accurate price extraction
        const preprocessed = preprocessedLocations[name] || location;

        // If size hasn't been explicitly selected, use cheapest price across all sizes
        let locPrice: number | null = null;
        if (!sizeExplicitlySelected) {
          locPrice = getCheapestPriceByTypeId(preprocessed, activeTypeId);
        } else {
          locPrice = getLocationPriceByFilter(preprocessed, activeTypeId, selectedSize);
        }

        // Add milk surcharge if a specific milk option is selected
        if (locPrice !== null && selectedMilk) {
          const milkSurcharge = getMilkSurchargeByTypeId(preprocessed, activeTypeId, selectedMilk);
          locPrice = locPrice + milkSurcharge;
        }

        if (locPrice !== null) {
          filteredPrices.push(locPrice);
        }
      });
    }

    // First collect marker data for overlap filtering
    const markerData: Array<{ element: React.ReactNode; price: number | null; lat: number; lng: number }> = [];

    filteredLocations.forEach(([name, location]: [string, any]) => {
      let price: number | null = null;
      if (hasFilters && activeTypeId) {
        // Use preprocessed location for better price extraction
        const preprocessed = preprocessedLocations[name] || location;

        // If size hasn't been explicitly selected, use cheapest price across all sizes
        // Otherwise, use the selected size
        if (!sizeExplicitlySelected) {
          price = getCheapestPriceByTypeId(preprocessed, activeTypeId);
        } else {
          price = getLocationPriceByFilter(preprocessed, activeTypeId, selectedSize);
        }

        // Add milk surcharge if a specific milk option is selected
        if (price !== null && selectedMilk) {
          const milkSurcharge = getMilkSurchargeByTypeId(preprocessed, activeTypeId, selectedMilk);
          price = price + milkSurcharge;
        }

        // Debug: log first location to see data structure
        if (filteredLocations[0][0] === name) {
          // console.log('[CoffeeMapper2] Sample location data:', {
          //   name,
          //   coffee_menu: location.coffee_menu,
          //   preprocessed_coffee_menu: preprocessed.coffee_menu,
          //   typeId: activeTypeId,
          //   size: selectedSize,
          //   milkOption: selectedMilk,
          //   price,
          // });
        }
      }

      // Get review rating from location
      const reviewRating = location.review_rating;
      const hasRating = reviewRating !== undefined && reviewRating !== null;
      const ratingValue = hasRating
        ? typeof reviewRating === 'number'
          ? reviewRating
          : parseFloat(String(reviewRating))
        : null;
      const ratingText = ratingValue !== null ? ratingValue.toFixed(1) : '';

      // Determine marker color based on display mode
      let markerColor = '#7c3aed'; // Default purple
      if (pinDisplayMode === 'rating' && ratingValue !== null) {
        // Color based on rating: green (4-5), orange (3-4), yellow (2-3), red (other)
        if (ratingValue >= 4) {
          markerColor = '#4CAF50'; // Green
        } else if (ratingValue >= 3) {
          markerColor = '#FF9800'; // Orange
        } else if (ratingValue >= 2) {
          markerColor = '#FFC107'; // Yellow
        } else {
          markerColor = '#F44336'; // Red
        }
      } else if (pinDisplayMode === 'price' && price !== null && filteredPrices.length > 0) {
        // Color based on price quartiles (existing logic)
        markerColor = getPriceColor(price, filteredPrices);
      }

      const priceText = price !== null ? `$${price.toFixed(2)}` : '';

      // Determine what to show on top and bottom based on display mode
      const showPriceOnTop = pinDisplayMode === 'price' && price !== null;
      const showRatingOnTop = pinDisplayMode === 'rating' && hasRating;
      const showPriceOnBottom = pinDisplayMode === 'rating' && price !== null;
      const showRatingOnBottom = pinDisplayMode === 'price' && hasRating;

      // Check if we have both top and bottom content
      const hasTopAndBottom = (showPriceOnTop && showRatingOnBottom) || (showRatingOnTop && showPriceOnBottom);

      // Adjust marker size - square pins, larger when both are present
      const hasContent = price !== null || hasRating;
      const markerSize = hasTopAndBottom ? 42 : hasContent ? 36 : 32;
      const fontSize = hasTopAndBottom ? '10px' : showPriceOnTop || showRatingOnTop ? '11px' : '0px';
      const bottomFontSize = hasTopAndBottom ? '9px' : showPriceOnBottom || showRatingOnBottom ? '11px' : '0px';

      const customIcon = divIcon({
        html: `
          <div class="custom-marker" style="
            width: ${markerSize}px;
            height: ${markerSize}px;
            border-radius: 4px;
            background: ${markerColor};
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            cursor: pointer;
            transition: all 0.2s;
            overflow: hidden;
          ">
            ${
              showPriceOnTop
                ? `
              <div style="
                width: 100%;
                height: ${hasTopAndBottom ? '60%' : '100%'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                line-height: 1;
                border-bottom: ${hasTopAndBottom ? '1px solid rgba(255,255,255,0.3)' : 'none'};
              ">${priceText}</div>
            `
                : ''
            }
            ${
              showRatingOnTop
                ? `
              <div style="
                width: 100%;
                height: ${hasTopAndBottom ? '60%' : '100%'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${fontSize};
                line-height: 1;
                border-bottom: ${hasTopAndBottom ? '1px solid rgba(255,255,255,0.3)' : 'none'};
                opacity: 0.95;
                gap: 2px;
              "><span style="font-size: ${fontSize};">★</span>${ratingText}</div>
            `
                : ''
            }
            ${
              showPriceOnBottom
                ? `
              <div style="
                width: 100%;
                height: ${hasTopAndBottom ? '40%' : '100%'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${bottomFontSize};
                line-height: 1;
                opacity: 0.95;
              ">${priceText}</div>
            `
                : ''
            }
            ${
              showRatingOnBottom
                ? `
              <div style="
                width: 100%;
                height: ${hasTopAndBottom ? '40%' : '100%'};
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: ${bottomFontSize};
                line-height: 1;
                opacity: 0.95;
                gap: 2px;
              "><span style="font-size: ${bottomFontSize};">★</span>${ratingText}</div>
            `
                : ''
            }
          </div>
        `,
        className: 'custom-marker-container',
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize / 2, markerSize / 2],
      });

      const markerElement = (
        <Marker
          key={name}
          position={[location.latitude, location.longitude]}
          icon={customIcon}
          eventHandlers={{
            click: async () => {
              // Format location and trigger modal (same pattern as App.tsx handleMarkerClick)
              const formattedLocation = {
                _id: name,
                name: name,
                ...location,
              };
              // Call handleCoffeeLocationClick to fetch details and open modal
              // Use ref to access the function (it's defined later in the component)
              if (handleCoffeeLocationClickRef.current) {
                await handleCoffeeLocationClickRef.current(formattedLocation, null, null);
              }
            },
          }}
        />
      );

      markerData.push({
        element: markerElement,
        price,
        lat: location.latitude,
        lng: location.longitude,
      });
    });

    setMarkerData(markerData);
    // Apply overlap filtering to get final markers
    const finalMarkers = filterOverlappingMarkers(markerData);
    setCoffeeMarkers(finalMarkers);
  }, [
    allCoffeeLocations,
    preprocessedLocations,
    activeTypeId,
    selectedSize,
    sizeExplicitlySelected,
    selectedMilk,
    priceRange,
    minRating,
    availablePriceRange,
    showPopularOnly,
    pinDisplayMode,
  ]);

  useEffect(() => {
    createCoffeeMarkers();
  }, [
    activeTypeId,
    selectedSize,
    sizeExplicitlySelected,
    selectedMilk,
    priceRange,
    minRating,
    showPopularOnly,
    pinDisplayMode,
    allCoffeeLocations,
    preprocessedLocations,
  ]);

  // Re-run marker creation when zoom changes (for overlap filtering)
  useEffect(() => {
    if (currentZoom >= 15) {
      // Only update markers at high zoom levels where overlap filtering is active
      const finalMarkers = filterOverlappingMarkers(markerData);
      setCoffeeMarkers(finalMarkers);
    }
  }, [currentZoom]);

  // Handle coffee location marker click (matches handleMarkerClick pattern from App.tsx - regular function, not useCallback)
  const handleCoffeeLocationClick = async (location: any, shop: any = null, details: any = null) => {
    // console.log('📍 Coffee location marker clicked:', location);
    let locationDetails = details;
    const locationName = location?._id || location?.name;

    if (!details || (details && shop)) {
      // Check cache first to avoid duplicate Firebase calls
      if (locationName && coffeeLocationDetailsCacheRef.current.has(locationName)) {
        locationDetails = coffeeLocationDetailsCacheRef.current.get(locationName);
      } else if (locationName && !pendingCoffeeLocationRequestsRef.current.has(locationName)) {
        // Always fetch full details from Firebase to get business_hours and other complete data
        // Even if light data exists, we need full details for the modal
        pendingCoffeeLocationRequestsRef.current.add(locationName);
        try {
          locationDetails = await getData(`/coffeelocations/${locationName}`);
          // console.log('📦 Fetched coffee location details:', locationName, locationDetails);
          // Cache the result
          if (locationName && locationDetails) {
            coffeeLocationDetailsCacheRef.current.set(locationName, locationDetails);
          }
        } catch (error) {
          console.error('❌ Error fetching coffee location details:', error);
          // If fetch fails, fall back to light data if available
          if (allCoffeeLocations[locationName]) {
            locationDetails = allCoffeeLocations[locationName];
          }
        } finally {
          pendingCoffeeLocationRequestsRef.current.delete(locationName);
        }
      } else if (locationName && pendingCoffeeLocationRequestsRef.current.has(locationName)) {
        // Request already pending, skip this call
        return;
      } else if (locationName && allCoffeeLocations[locationName]) {
        // Fallback: use light data if no fetch was made (shouldn't happen, but safety check)
        locationDetails = allCoffeeLocations[locationName];
      }
    }

    if (!isMountedRef.current) return;

    if (locationDetails) {
      // console.log('✅ Setting selectedCoffeeLocation with business_hours:', locationDetails.business_hours);
      setSelectedCoffeeLocation({ _id: locationName, ...locationDetails });
    } else if (location) {
      setSelectedCoffeeLocation({ _id: locationName, ...location });
    }
  };

  // Update ref after function is defined so it can be accessed in createCoffeeMarkers
  handleCoffeeLocationClickRef.current = handleCoffeeLocationClick;

  // Auto-open modal when locationToOpen prop changes (from Home2 coffee card click)
  useEffect(() => {
    if (locationToOpen) {
      const locationName = locationToOpen.name || locationToOpen._id || locationToOpen.location;
      if (locationName && locationToOpen.latitude && locationToOpen.longitude) {
        const location: LatLngTuple = [locationToOpen.latitude, locationToOpen.longitude];

        // Wait for map to be ready, then center and zoom (same as handleSelectCafe from list view)
        const centerAndOpen = () => {
          if (mapRef.current) {
            mapRef.current.setView(location, 19, {
              animate: true,
              duration: 0.8,
            });
          } else {
            // Fallback to state update
            setMapCenter(location);
            setMapZoom(19);
          }

          // Format location and open modal (same pattern as handleSelectCafe)
          const formattedLocation = {
            _id: locationName,
            name: locationName,
            ...locationToOpen,
          };

          // Open the modal after map has centered (same delay as handleSelectCafe)
          setTimeout(() => {
            handleCoffeeLocationClick(formattedLocation, null, null);
            // Clear locationToOpen after opening to prevent reopening
            if (onLocationOpened) {
              onLocationOpened();
            }
          }, 300);
        };

        // Wait a bit for the component to mount and map to initialize
        if (mapRef.current) {
          centerAndOpen();
        } else {
          // If map not ready, wait a bit and try again
          const timeoutId = setTimeout(() => {
            centerAndOpen();
          }, 100);
          return () => clearTimeout(timeoutId);
        }
      }
    }
  }, [locationToOpen, onLocationOpened]);

  // Create markers for coffee locations with filtering (matches App.tsx createAllMarkers pattern)
  // Note: showPopularOnly is captured from component scope via closure
  const createCoffeeLocationMarkers = useCallback(
    (
      locations: Record<string, any>,
      type: string,
      backgroundColor: string,
      handleClick,
      setMarkers,
      filters?: {
        selectedCoffeeFilter: string | null;
        selectedSize: NormalizedSize;
        priceRange: { min: number; max: number } | null;
        minRating?: number | null;
      },
      priceRangeData?: { min: number; max: number },
      preprocessed?: Record<string, any>
    ) => {
      const hasFilters = filters && filters.selectedCoffeeFilter !== null;
      // Note: filters.selectedCoffeeFilter is now activeTypeId

      // First filter locations to get the filtered set
      const filteredLocationEntries = Object.entries(locations).filter(([name, location]: [string, any]) => {
        if (!location) return false;

        // Check coordinates
        const lat = location.latitude;
        const lng = location.longitude;
        if (lat === undefined || lat === null || isNaN(lat) || lng === undefined || lng === null || isNaN(lng)) {
          return false;
        }

        // Filter by popular cafes if enabled
        if (showPopularOnly) {
          const isPopular = POPULAR_CAFES.some(
            (popularName) =>
              name.toLowerCase().includes(popularName.toLowerCase()) ||
              popularName.toLowerCase().includes(name.toLowerCase())
          );
          if (!isPopular) {
            return false;
          }
        }

        // Apply filters if provided
        if (filters) {
          const { selectedCoffeeFilter, selectedSize, priceRange } = filters;

          // If no filters selected, show all locations
          if (!selectedCoffeeFilter) {
            return true;
          }

          // Check if location has items matching the selected filter (using preprocessed data)
          // Fast filtering using preprocessed typeId
          const preprocessedData = (preprocessed && preprocessed[name]) || location;
          let hasMatchingType = false;

          // Check coffee_menu format
          if (preprocessedData.coffee_menu && typeof preprocessedData.coffee_menu === 'object') {
            hasMatchingType = Object.values(preprocessedData.coffee_menu).some(
              (menuItem: any) =>
                menuItem &&
                typeof menuItem === 'object' &&
                (menuItem._typeId === selectedCoffeeFilter || menuItem.typeId === selectedCoffeeFilter)
            );
          }

          // Check legacy format
          if (!hasMatchingType && preprocessedData.coffeeHotDrinks && Array.isArray(preprocessedData.coffeeHotDrinks)) {
            hasMatchingType = preprocessedData.coffeeHotDrinks.some(
              (drink: any) => drink.typeId === selectedCoffeeFilter
            );
          }

          if (!hasMatchingType && preprocessedData.icedCoffeeTea && Array.isArray(preprocessedData.icedCoffeeTea)) {
            hasMatchingType = preprocessedData.icedCoffeeTea.some(
              (drink: any) => drink.typeId === selectedCoffeeFilter
            );
          }

          if (!hasMatchingType) {
            return false;
          }

          // Check price if price range filter is set
          if (priceRange) {
            const price = getLocationPriceByFilter(preprocessedData, selectedCoffeeFilter, selectedSize);
            if (price === null || price < priceRange.min || price > priceRange.max) {
              return false;
            }
          }

          // Exclude prices < $3
          const price = getLocationPriceByFilter(preprocessedData, selectedCoffeeFilter, selectedSize);
          if (price !== null && price < 3) {
            return false;
          }

          // Filter by minimum rating if set
          if (filters && 'minRating' in filters && filters.minRating !== null && filters.minRating !== undefined) {
            const locationRating = location.review_rating;
            if (locationRating === undefined || locationRating === null) {
              return false; // Exclude locations without ratings when filter is active
            }
            const ratingValue =
              typeof locationRating === 'number' ? locationRating : parseFloat(String(locationRating));
            if (isNaN(ratingValue) || ratingValue < filters.minRating) {
              return false;
            }
          }

          return true;
        }

        return true;
      });

      // Calculate all filtered prices for quartile-based coloring
      const filteredPricesForQuartiles: number[] = [];
      if (hasFilters && filteredLocationEntries.length > 0 && filters && filters.selectedCoffeeFilter) {
        filteredLocationEntries.forEach(([name, location]: [string, any]) => {
          // Use preprocessed location for accurate price extraction
          const preprocessedData = (preprocessed && preprocessed[name]) || location;
          const locPrice = getLocationPriceByFilter(
            preprocessedData,
            filters.selectedCoffeeFilter,
            filters.selectedSize
          );
          if (locPrice !== null) {
            filteredPricesForQuartiles.push(locPrice);
          }
        });
      }

      const markers = filteredLocationEntries.map(([name, location]: [string, any]) => {
        const prop = { _id: name, ...location };

        // Get price if filters are applied
        let price: number | null = null;
        let markerColor = backgroundColor;
        let priceText = '';

        if (hasFilters && filters && filters.selectedCoffeeFilter) {
          // Use preprocessed location for better price extraction
          const preprocessedData = (preprocessed && preprocessed[name]) || location;
          price = getLocationPriceByFilter(preprocessedData, filters.selectedCoffeeFilter, filters.selectedSize);

          // Apply color based on quartiles whenever price is available (regardless of zoom)
          if (price !== null && filteredPricesForQuartiles.length > 0) {
            markerColor = getPriceColor(price, filteredPricesForQuartiles);
            priceText = `$${price.toFixed(2)}`;
          }
        }

        // Get review rating from location
        const reviewRating = location.review_rating;
        const hasRating = reviewRating !== undefined && reviewRating !== null;
        const ratingText = hasRating
          ? typeof reviewRating === 'number'
            ? reviewRating.toFixed(1)
            : String(reviewRating)
          : '';

        // Adjust marker size - square pins, larger when both are present
        const hasContent = price !== null || hasRating;
        const hasBoth = price !== null && hasRating;
        const markerSize = hasBoth ? 42 : hasContent ? 36 : 32;
        const fontSize = hasBoth ? '10px' : price !== null ? '11px' : '0px';
        const ratingFontSize = hasBoth ? '9px' : '0px';

        // Create icon with price and rating if available
        const locationIcon = divIcon({
          html: `
              <div style="
                width: ${markerSize}px;
                height: ${markerSize}px;
                border-radius: 4px;
                background: ${markerColor};
                border: 2px solid white;
                box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                color: white;
                font-weight: bold;
                cursor: pointer;
                overflow: hidden;
              ">
                ${
                  price !== null
                    ? `
                  <div style="
                    width: 100%;
                    height: ${hasBoth ? '50%' : '100%'};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: ${fontSize};
                    line-height: 1;
                    border-bottom: ${hasBoth ? '1px solid rgba(255,255,255,0.3)' : 'none'};
                  ">${priceText}</div>
                `
                    : ''
                }
                ${
                  hasRating
                    ? `
                  <div style="
                    width: 100%;
                    height: ${hasBoth ? '50%' : '100%'};
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: ${ratingFontSize};
                    line-height: 1;
                    opacity: 0.95;
                  ">⭐${ratingText}</div>
                `
                    : ''
                }
              </div>
            `,
          className: 'custom-marker-container',
          iconSize: [markerSize, markerSize],
          iconAnchor: [markerSize / 2, markerSize / 2],
        });

        return (
          <Marker
            key={name}
            position={[location.latitude, location.longitude]}
            eventHandlers={{
              click: async () => handleClick(prop),
            }}
            icon={locationIcon}
          />
        );
      });
      setMarkers(markers);
    },
    [showPopularOnly] // Include showPopularOnly so callback recreates when it changes
  );

  // Preprocess locations when allCoffeeLocations changes (only if not provided as prop)
  useEffect(() => {
    if (preprocessedCoffeeLocationsProp) {
      // Preprocessed data provided from App.tsx, no need to preprocess - it's already done on app load
      return;
    }

    // Fallback: preprocess locally if prop not provided (for standalone usage)
    if (Object.keys(allCoffeeLocations).length > 0) {
      // Preprocess locations to add type_ids and category_ids
      const preprocessed: Record<string, any> = {};
      Object.entries(allCoffeeLocations).forEach(([name, location]: [string, any]) => {
        // First preprocess for type/category matching
        const typePreprocessed = preprocessLocation(location);
        // Then preprocess for size normalization
        const fullyPreprocessed = preprocessLocationSizes(typePreprocessed);
        preprocessed[name] = fullyPreprocessed;
      });
      setPreprocessedLocationsLocal(preprocessed);
    }
  }, [allCoffeeLocations, preprocessedCoffeeLocationsProp]);

  // Only fetch coffee locations if not provided as prop (fallback for standalone usage)
  useEffect(() => {
    if (allCoffeeLocationsProp) {
      // Data provided from App.tsx, no need to fetch - it's already loaded on app load
      return;
    }

    // Fallback: fetch locally if prop not provided (for standalone usage)
    const fetchCoffeeLocations = async () => {
      try {
        const data = await getData('/coffeelocationslight');
        setAllCoffeeLocationsLocal(data || {});
      } catch (error) {
        console.error('❌ Error fetching coffee locations:', error);
        setAllCoffeeLocationsLocal({});
      }
    };

    fetchCoffeeLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCoffeeLocationsProp]); // Only fetch if prop is not provided

  // Update coffee location markers when filters change
  // NOTE: This is disabled because createCoffeeMarkers already handles all marker creation
  // Having both creates duplicate markers, causing incorrect cluster counts
  // useEffect(() => {
  //   if (Object.keys(allCoffeeLocations).length > 0) {
  //     createCoffeeLocationMarkers(
  //       allCoffeeLocations,
  //       'basic',
  //       '#7c3aed',
  //       handleCoffeeLocationClick,
  //       setCoffeeLocationMarkers,
  //       { selectedCoffeeFilter: activeTypeId, selectedSize, priceRange },
  //       availablePriceRange,
  //       preprocessedLocations
  //     );
  //   }
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [allCoffeeLocations, activeTypeId, selectedSize, priceRange, availablePriceRange, createCoffeeLocationMarkers, preprocessedLocations, showPopularOnly]);

  // Cleanup on unmount
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Current location pin – centered and zoomed when button is used
  const createUserLocationMarker = useCallback((location: LatLngTuple) => {
    const customIcon = divIcon({
      html: `
        <div class="coffeemapper-user-location-pin">
          <span class="coffeemapper-user-location-pin__pulse"></span>
          <span class="coffeemapper-user-location-pin__ring"></span>
          <span class="coffeemapper-user-location-pin__dot"></span>
        </div>
      `,
      className: 'coffeemapper-user-location-pin-container',
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });

    const marker = <Marker key="user-location" position={location} icon={customIcon} />;
    setUserLocationMarker(marker);
  }, []);

  // Sync marker when app-wide userLocation (prop) changes
  useEffect(() => {
    if (userLocation) {
      createUserLocationMarker(userLocation as LatLngTuple);
    } else {
      setUserLocationMarker(null);
    }
  }, [userLocation, createUserLocationMarker]);

  const getCurrentLocation = (): Promise<LatLngTuple> => {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      // Check if we're on HTTPS or localhost (required for geolocation on mobile)
      const isSecureContext = window.isSecureContext || window.location.hostname === 'localhost';
      if (!isSecureContext) {
        reject(new Error('Geolocation requires HTTPS or localhost'));
        return;
      }

      setIsLocatingLocal(true);

      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          const location: LatLngTuple = [latitude, longitude];
          setUserLocationLocal(location);
          createUserLocationMarker(location);
          setIsLocatingLocal(false);
          resolve(location);
        },
        (error) => {
          setIsLocatingLocal(false);
          console.error('❌ Error getting current location:', error);

          // Provide more specific error messages
          let errorMessage = 'Unable to get your current location.';
          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access denied. Please enable location permissions in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS settings.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please try again.';
              break;
            default:
              errorMessage =
                'Unable to get your current location. Please check your location permissions and try again.';
          }

          reject(new Error(errorMessage));
        },
        {
          enableHighAccuracy: true,
          timeout: 15000, // Increased timeout for mobile
          maximumAge: 30000, // Reduced cache time for better accuracy
        }
      );
    });
  };

  const centerOnCurrentLocation = async () => {
    try {
      const zoomToLocation = 18; // Zoom in more around the user pin
      if (onRequestLocation) {
        const location = await onRequestLocation();
        if (mapRef.current) {
          mapRef.current.setView(location, zoomToLocation, { animate: true, duration: 0.8 });
        } else {
          setMapCenter(location as LatLngTuple);
          setMapZoom(zoomToLocation);
        }
        return;
      }
      if (navigator.permissions) {
        const permission = await navigator.permissions.query({ name: 'geolocation' });
        if (permission.state === 'denied') {
          throw new Error('Location access denied. Please enable location permissions in your browser settings.');
        }
      }

      const location = await getCurrentLocation();
      if (mapRef.current) {
        mapRef.current.setView(location, zoomToLocation, {
          animate: true,
          duration: 0.8,
        });
      } else {
        setMapCenter(location);
        setMapZoom(zoomToLocation);
      }

      // Show a brief success message
      const mapElement = document.querySelector('.leaflet-container');
      if (mapElement) {
        mapElement.classList.add('location-found');
        setTimeout(() => mapElement.classList.remove('location-found'), 2000);
      }
    } catch (error) {
      console.error('❌ Failed to get current location:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Unable to get your current location. Please check your location permissions and try again.';

      // Show error notification
      const notification = document.createElement('div');
      notification.className = 'location-error-notification';
      notification.innerHTML = `
        <div class="notification-content">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="8" x2="12" y2="12"></line>
            <line x1="12" y1="16" x2="12.01" y2="16"></line>
          </svg>
          <span>${errorMessage}</span>
        </div>
      `;
      document.body.appendChild(notification);
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 5000);
    }
  };

  // Filter handlers (keeping UI functionality)
  const handleFilterClick = (filter: FilterType) => {
    setActiveFilter(filter);
  };

  const handleCancelFilter = () => {
    setActiveFilter(null);
  };

  // Handle mode change - reset deeper selections
  // Use a ref to track if this is the initial mount
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      // On initial mount, preserve the default activeTypeId ('type_cappuccino_hot')
      return;
    }

    // Only reset on subsequent mode changes
    if (mode === 'popular') {
      setActiveCategoryId(null);
      // Don't reset activeTypeId on mode change - preserve user's selection
      // setActiveTypeId(null);
    } else if (mode === 'categories') {
      // Don't reset activeTypeId on mode change - preserve user's selection
      // setActiveTypeId(null);
      // Keep activeCategoryId until user selects one
    }
  }, [mode]);

  const handleClearFilter = () => {
    if (activeFilter === 'coffee') {
      setActiveTypeId(null);
      setActiveCategoryId(null);
      setTemperatureFilter(null);
      setMode('popular');
    } else if (activeFilter === 'size') {
      setSelectedSize('regular');
    } else if (activeFilter === 'milk') {
      setSelectedMilk(null);
    } else if (activeFilter === 'price') {
      setPriceRange(null);
    } else if (activeFilter === 'rating') {
      setMinRating(null);
    }
  };

  // Reset all filters
  const handleResetAllFilters = () => {
    setActiveTypeId(null);
    setActiveCategoryId(null);
    setTemperatureFilter(null);
    setMode('popular');
    setSelectedSize('regular');
    setSelectedMilk(null);
    setPriceRange(null);
    setMinRating(null);
    setShowPopularOnly(false);
    setActiveFilter(null);
  };

  // Check if any filters are active
  const hasActiveFilters =
    activeTypeId !== null || selectedMilk !== null || priceRange !== null || minRating !== null || showPopularOnly;

  // Milk filter handler
  const handleMilkSelect = (milk: string | null) => {
    setSelectedMilk(milk);
    // Don't close modal immediately - let user see the toggle animation and make multiple selections if needed
  };

  const handlePopularToggle = () => {
    setShowPopularOnly(!showPopularOnly);
  };

  // Handle mode change
  const handleModeChange = (newMode: 'popular' | 'categories') => {
    setMode(newMode);
    if (newMode === 'popular') {
      setActiveCategoryId(null);
      setActiveTypeId(null);
      setTemperatureFilter(null);
    } else {
      setActiveTypeId(null);
      setTemperatureFilter(null);
    }
  };

  // Get categories filtered by temperature
  const getCategoriesByTemperature = (temp: 'hot' | 'cold'): Category[] => {
    const hotCategoryIds = [
      'cat_milk_coffee',
      'cat_black_coffee',
      'cat_filter_coffee',
      'cat_espresso',
      'cat_hot_chocolate',
      'cat_chai',
      'cat_tea',
      'cat_matcha',
      'cat_kids',
      'cat_wellness',
    ];
    const coldCategoryIds = ['cat_cold_coffee', 'cat_iced_drinks'];

    const categoryIds = temp === 'hot' ? hotCategoryIds : coldCategoryIds;
    return getCategories().filter((cat) => categoryIds.includes(cat.id));
  };

  // Get all cold drink types (combines types from both cold categories)
  const getAllColdTypes = (): Type[] => {
    const coldCategoryIds = ['cat_cold_coffee', 'cat_iced_drinks'];
    const allColdTypes: Type[] = [];

    coldCategoryIds.forEach((categoryId) => {
      const types = getTypesForCategory(categoryId);
      allColdTypes.push(...types);
    });

    // Sort by priority
    return allColdTypes.sort((a, b) => a.priority - b.priority);
  };

  // Handle temperature selection
  const handleTemperatureSelect = (temp: 'hot' | 'cold') => {
    setTemperatureFilter(temp);
    setActiveCategoryId(null);
    setActiveTypeId(null);
  };

  // Handle category selection
  const handleCategorySelect = (categoryId: string) => {
    setActiveCategoryId(categoryId);
    setActiveTypeId(null); // Reset type when category changes
  };

  // Handle type selection
  const handleTypeSelect = (typeId: string) => {
    setActiveTypeId(typeId);
    setActiveFilter(null); // Close modal after selection
  };

  const handleSizeToggle = (size: NormalizedSize) => {
    setSelectedSize(size);
    setSizeExplicitlySelected(true);
    setActiveFilter(null); // Close modal after selection
  };

  const handlePresetPriceRange = (range: { min: number; max: number } | null) => {
    setPriceRange(range);
    if (range) {
      setSliderMin(range.min);
      setSliderMax(range.max);
    } else {
      // Reset to full range when "All Prices" is selected
      setSliderMin(SLIDER_PRICE_MIN);
      setSliderMax(SLIDER_PRICE_MAX);
    }
    setActiveFilter(null); // Close modal after selection
  };

  const handleRatingSelect = (rating: number | null) => {
    setMinRating(rating);
    setActiveFilter(null); // Close modal after selection
  };

  // Initialize slider values when price filter modal opens
  useEffect(() => {
    if (activeFilter === 'price') {
      if (priceRange) {
        setSliderMin(priceRange.min);
        setSliderMax(priceRange.max);
      } else {
        setSliderMin(SLIDER_PRICE_MIN);
        setSliderMax(SLIDER_PRICE_MAX);
      }
    }
  }, [activeFilter, priceRange]);

  const handleSliderChange = (type: 'min' | 'max', value: number) => {
    if (type === 'min') {
      const newMin = Math.min(value, sliderMax - 0.1); // Ensure min doesn't exceed max
      setSliderMin(newMin);
      setPriceRange({ min: newMin, max: sliderMax });
    } else {
      const newMax = Math.max(value, sliderMin + 0.1); // Ensure max doesn't go below min
      setSliderMax(newMax);
      setPriceRange({ min: sliderMin, max: newMax });
    }
  };

  const handleSelectCafe = async (cafe: CoffeeCafe) => {
    setSelectedCafe(cafe);
    setShowCoffeeSearch(false);
    setShowCoffeeList(false);

    // Center map on the selected coffee location (with animation)
    if (cafe.latitude && cafe.longitude) {
      const location: LatLngTuple = [cafe.latitude, cafe.longitude];
      if (mapRef.current) {
        mapRef.current.setView(location, 19, {
          animate: true,
          duration: 0.8,
        });
      } else {
        // Fallback to state update
        setMapCenter(location);
        setMapZoom(19);
      }
    }

    // Trigger coffee location modal (same pattern as list view - format location and trigger handler)
    const locationName = cafe.name;
    const location = allCoffeeLocations[locationName] || cafe;
    const formattedLocation = {
      _id: locationName,
      name: locationName,
      ...location,
    };
    await handleCoffeeLocationClick(formattedLocation, null, null); // Call handler to fetch details and open modal
  };

  // Get filtered cafes for list view (using preprocessed locations for fast filtering)
  const getFilteredCafes = useMemo(() => {
    const filtered = Object.entries(allCoffeeLocations)
      .filter(([name, location]: [string, any]) => {
        const lat = location.latitude;
        const lng = location.longitude;
        if (lat === undefined || lat === null || isNaN(lat) || lng === undefined || lng === null || isNaN(lng)) {
          return false;
        }

        // Filter by popular cafes if enabled
        if (showPopularOnly) {
          const isPopular = POPULAR_CAFES.some(
            (popularName) =>
              name.toLowerCase().includes(popularName.toLowerCase()) ||
              popularName.toLowerCase().includes(name.toLowerCase())
          );
          if (!isPopular) {
            return false;
          }
        }

        // If no type filter is selected, show all cafes (or just popular if filter is on)
        if (!activeTypeId) {
          return true;
        }

        // If type is selected, check if location has matching type
        // Fast filtering using preprocessed typeId
        const preprocessed = preprocessedLocations[name] || location;
        let hasMatchingType = false;

        // Check coffee_menu format (check both _typeId and typeId for compatibility)
        if (preprocessed.coffee_menu && typeof preprocessed.coffee_menu === 'object') {
          hasMatchingType = Object.values(preprocessed.coffee_menu).some(
            (menuItem: any) =>
              menuItem &&
              typeof menuItem === 'object' &&
              (menuItem._typeId === activeTypeId || menuItem.typeId === activeTypeId)
          );
        }

        // Check legacy format
        if (!hasMatchingType && preprocessed.coffeeHotDrinks && Array.isArray(preprocessed.coffeeHotDrinks)) {
          hasMatchingType = preprocessed.coffeeHotDrinks.some((drink: any) => drink.typeId === activeTypeId);
        }

        if (!hasMatchingType && preprocessed.icedCoffeeTea && Array.isArray(preprocessed.icedCoffeeTea)) {
          hasMatchingType = preprocessed.icedCoffeeTea.some((drink: any) => drink.typeId === activeTypeId);
        }

        if (!hasMatchingType) {
          return false;
        }

        // Check price if price range filter is set
        if (priceRange) {
          // Use preprocessed location for better price extraction
          const preprocessedForPrice = preprocessedLocations[name] || location;
          const price = getLocationPriceByFilter(preprocessedForPrice, activeTypeId, selectedSize);
          if (price === null || price < priceRange.min || price > priceRange.max) {
            return false;
          }
        }

        // Exclude prices < $3
        if (activeTypeId) {
          const preprocessedForPrice = preprocessedLocations[name] || location;
          const price = getLocationPriceByFilter(preprocessedForPrice, activeTypeId, selectedSize);
          if (price !== null && price < 3) {
            return false;
          }
        }

        // Filter by milk option if selected (only for items matching the active type)
        if (selectedMilk) {
          const preprocessed = preprocessedLocations[name] || location;
          if (!locationHasMilkOption(preprocessed, selectedMilk, activeTypeId)) {
            return false;
          }
        }

        // Filter by minimum rating if set
        if (minRating !== null) {
          const locationRating = location.review_rating;
          if (locationRating === undefined || locationRating === null) {
            return false; // Exclude locations without ratings when filter is active
          }
          const ratingValue = typeof locationRating === 'number' ? locationRating : parseFloat(String(locationRating));
          if (isNaN(ratingValue) || ratingValue < minRating) {
            return false;
          }
        }

        return true;
      })
      .map(([name, location]: [string, any]) => ({ name, ...location }));

    return filtered;
  }, [
    allCoffeeLocations,
    preprocessedLocations,
    activeTypeId,
    selectedSize,
    selectedMilk,
    priceRange,
    minRating,
    showPopularOnly,
  ]);

  const getSelectedCoffeeCount = () => {
    return activeTypeId ? 1 : 0;
  };

  const getPriceRangeText = () => {
    if (!priceRange) return 'All';
    return `$${priceRange.min.toFixed(1)} - $${priceRange.max.toFixed(1)}`;
  };

  const getSortedCafes = useMemo(() => {
    // Use referenceLocation (from Home2) if available, otherwise fall back to userLocation
    const currentLocation = referenceLocation || userLocation || null;

    return getFilteredCafes
      .map((cafe: any) => {
        let price: number | null = null;
        let distance: number | null = null;

        // Calculate price when filters are applied (use preprocessed location for better accuracy)
        if (activeTypeId && selectedSize) {
          // Use preprocessed location if available for better price extraction
          const preprocessed = preprocessedLocations[cafe.name] || cafe;

          // If size hasn't been explicitly selected, use cheapest price across all sizes
          if (!sizeExplicitlySelected) {
            price = getCheapestPriceByTypeId(preprocessed, activeTypeId);
          } else {
            price = getLocationPriceByFilter(preprocessed, activeTypeId, selectedSize);
          }

          // Add milk surcharge if a specific milk option is selected
          if (price !== null && selectedMilk) {
            const milkSurcharge = getMilkSurchargeByTypeId(preprocessed, activeTypeId, selectedMilk);
            price = price + milkSurcharge;
          }
        }

        // Calculate distance from referenceLocation (Home2) or userLocation
        if (currentLocation) {
          distance = calculateDistance(currentLocation[0], currentLocation[1], cafe.latitude, cafe.longitude);
        }

        // Get rating from cafe
        const rating = cafe.review_rating;
        const ratingValue =
          rating !== undefined && rating !== null
            ? typeof rating === 'number'
              ? rating
              : parseFloat(String(rating))
            : null;

        return { cafe, price, distance, rating: ratingValue };
      })
      .sort((a, b) => {
        if (listSortBy === 'price') {
          if (a.price === null && b.price === null) return 0;
          if (a.price === null) return 1;
          if (b.price === null) return -1;
          // Use priceSortOrder to determine sort direction
          return priceSortOrder === 'lowest' ? a.price - b.price : b.price - a.price;
        } else if (listSortBy === 'rating') {
          // Sort by rating (highest first)
          if (a.rating === null && b.rating === null) return 0;
          if (a.rating === null) return 1;
          if (b.rating === null) return -1;
          return b.rating - a.rating; // Higher ratings first
        } else {
          // When popular filter is on, prioritize popular cafes
          if (showPopularOnly) {
            const aIsPopular = POPULAR_CAFES.some(
              (popularName) =>
                a.cafe.name.toLowerCase().includes(popularName.toLowerCase()) ||
                popularName.toLowerCase().includes(a.cafe.name.toLowerCase())
            );
            const bIsPopular = POPULAR_CAFES.some(
              (popularName) =>
                b.cafe.name.toLowerCase().includes(popularName.toLowerCase()) ||
                popularName.toLowerCase().includes(b.cafe.name.toLowerCase())
            );
            if (aIsPopular && !bIsPopular) return -1;
            if (!aIsPopular && bIsPopular) return 1;
          }
          if (a.distance === null && b.distance === null) return 0;
          if (a.distance === null) return 1;
          if (b.distance === null) return -1;
          return a.distance - b.distance;
        }
      });
  }, [
    getFilteredCafes,
    preprocessedLocations,
    activeTypeId,
    selectedSize,
    sizeExplicitlySelected,
    selectedMilk,
    referenceLocation,
    userLocation,
    listSortBy,
    priceSortOrder,
    showPopularOnly,
  ]);

  // Get displayed items (paginated)
  const displayedItems = useMemo(() => {
    return getSortedCafes.slice(0, displayedCount);
  }, [getSortedCafes, displayedCount]);

  // Infinite scroll using Intersection Observer
  useEffect(() => {
    if (!showCoffeeList) return; // Only observe when list is visible

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && displayedCount < getSortedCafes.length) {
          // Load more items
          setDisplayedCount((prev) => Math.min(prev + ITEMS_PER_PAGE, getSortedCafes.length));
        }
      },
      {
        root: null,
        rootMargin: '100px', // Start loading before reaching the bottom
        threshold: 0.1,
      }
    );

    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [displayedCount, getSortedCafes.length, showCoffeeList]);

  // Reset displayed count when filters or sort changes
  useEffect(() => {
    setDisplayedCount(ITEMS_PER_PAGE);
  }, [
    activeTypeId,
    selectedSize,
    sizeExplicitlySelected,
    selectedMilk,
    priceRange,
    listSortBy,
    priceSortOrder,
    userLocation,
  ]);

  // Helper function to format opening hours
  const formatOpeningHours = (hours: any): string => {
    if (!hours) return '';

    if (typeof hours === 'string') {
      return hours;
    }

    if (Array.isArray(hours)) {
      // Handle array format: [{day: "Monday", start: "07:00:00", end: "15:00:00"}, ...]
      const formatTime = (timeStr: string): string => {
        if (!timeStr) return '';
        // Handle both "HH:MM:SS" and "HH:MM" formats
        const parts = timeStr.split(':');
        const hours = parts[0];
        const minutes = parts[1] || '00';
        const hour = parseInt(hours, 10);
        const period = hour >= 12 ? 'PM' : 'AM';
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${displayHour}:${minutes} ${period}`;
      };

      const formatDayHours = (dayData: any): string => {
        const start = dayData.start || dayData.Start || dayData.startTime || '';
        const end = dayData.end || dayData.End || dayData.endTime || '';
        // Check if closed (both times are 00:00:00 or empty)
        if ((start === '00:00:00' || !start) && (end === '00:00:00' || !end)) {
          return 'Closed';
        }
        if (!start || !end) return 'Closed';
        return `${formatTime(start)} - ${formatTime(end)}`;
      };

      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      const weekdayHours: Array<{ day: string; hours: string }> = [];
      const weekendHours: Array<{ day: string; hours: string }> = [];
      const seenDays = new Set<string>();

      hours.forEach((dayData: any) => {
        const day = dayData.day || dayData.Day || '';

        // Skip if we've already processed this day (deduplicate)
        if (!day || seenDays.has(day)) {
          return;
        }

        seenDays.add(day);
        const hoursStr = formatDayHours(dayData);

        if (weekdays.includes(day)) {
          weekdayHours.push({ day, hours: hoursStr });
        } else {
          weekendHours.push({ day, hours: hoursStr });
        }
      });

      // Check if all weekdays have the same hours
      if (weekdayHours.length === 5) {
        const allSame = weekdayHours.every((w) => w.hours === weekdayHours[0].hours);
        if (allSame) {
          let result = `Monday-Friday: ${weekdayHours[0].hours}`;
          if (weekendHours.length > 0) {
            const weekendStr = weekendHours.map((w) => `${w.day}: ${w.hours}`).join('\n');
            result += `\n${weekendStr}`;
          }
          return result;
        }
      }

      // Otherwise, list all days
      return [...weekdayHours, ...weekendHours].map((w) => `${w.day}: ${w.hours}`).join('\n');
    }

    if (typeof hours === 'object' && hours !== null) {
      // Handle object format: {Monday: "7am-5pm", ...}
      const entries = Object.entries(hours);
      if (entries.length === 0) return '';

      const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
      const weekdayHours: string[] = [];
      const weekendHours: Array<{ day: string; hours: string }> = [];

      entries.forEach(([day, hoursStr]) => {
        if (weekdays.includes(day)) {
          weekdayHours.push(hoursStr as string);
        } else {
          weekendHours.push({ day, hours: hoursStr as string });
        }
      });

      // Check if all weekdays have the same hours
      if (weekdayHours.length === 5) {
        const allSame = weekdayHours.every((h) => h === weekdayHours[0]);
        if (allSame) {
          let result = `Monday-Friday: ${weekdayHours[0]}`;
          if (weekendHours.length > 0) {
            const weekendStr = weekendHours.map((w) => `${w.day}: ${w.hours}`).join('\n');
            result += `\n${weekendStr}`;
          }
          return result;
        }
      }

      // Otherwise, list all days
      const allDays = [
        ...weekdayHours.map((h, i) => `${weekdays[i]}: ${h}`),
        ...weekendHours.map((w) => `${w.day}: ${w.hours}`),
      ];
      return allDays.join('\n');
    }

    return '';
  };

  // Render modal content for selected coffee location (simplified version)
  const renderCoffeeLocationModalContent = () => {
    if (!selectedCoffeeLocation) return null;

    // Check if filters are active
    const hasActiveFilter = activeTypeId !== null;

    // Prepare and sort coffee menu items (from coffee_menu format)
    const coffeeMenuItems = selectedCoffeeLocation.coffee_menu
      ? Object.entries(selectedCoffeeLocation.coffee_menu).map(([itemName, menuItem]: [string, any]) => {
          // Extract sizes from new format (menuItem.Sizes) or old format (menuItem directly)
          const rawSizes = menuItem?.Sizes || menuItem;

          // Filter out metadata fields (_typeId, _categoryId, typeId, categoryId, Sizes)
          const sizes =
            typeof rawSizes === 'object' && rawSizes !== null
              ? Object.fromEntries(
                  Object.entries(rawSizes).filter(
                    ([key]) =>
                      key !== '_typeId' &&
                      key !== '_categoryId' &&
                      key !== 'typeId' &&
                      key !== 'categoryId' &&
                      key !== 'Sizes'
                  )
                )
              : rawSizes;

          // Check if this item matches the active filter
          // Use preprocessed typeId if available
          const preprocessed = preprocessedLocations[selectedCoffeeLocation._id || selectedCoffeeLocation.name];
          let matchesFilter = false;
          if (hasActiveFilter && activeTypeId && preprocessed) {
            const menuItem = preprocessed.coffee_menu?.[itemName];
            if (
              menuItem &&
              typeof menuItem === 'object' &&
              (menuItem._typeId === activeTypeId || menuItem.typeId === activeTypeId)
            ) {
              matchesFilter = true;
            }
          }

          // Get the price for the selected size if filter is active
          let filteredPrice: number | null = null;
          if (matchesFilter && selectedSize) {
            filteredPrice = getLocationPriceByName(selectedCoffeeLocation, itemName, selectedSize);
          }

          return {
            itemName,
            sizes,
            matchesFilter,
            filteredPrice,
            format: 'coffee_menu' as const,
          };
        })
      : [];

    // Prepare legacy format items (coffeeHotDrinks and icedCoffeeTea)
    const legacyItems: Array<{
      itemName: string;
      sizes: any;
      matchesFilter: boolean;
      filteredPrice: number | null;
      format: 'hot' | 'iced';
    }> = [];

    if (selectedCoffeeLocation.coffeeHotDrinks && Array.isArray(selectedCoffeeLocation.coffeeHotDrinks)) {
      selectedCoffeeLocation.coffeeHotDrinks.forEach((drink: any) => {
        if (drink.name) {
          // Use preprocessed typeId if available
          const preprocessed = preprocessedLocations[selectedCoffeeLocation._id || selectedCoffeeLocation.name];
          let matchesFilter = false;
          if (hasActiveFilter && activeTypeId && preprocessed) {
            const drinkItem = preprocessed.coffeeHotDrinks?.find((d: any) => d.name === drink.name);
            if (drinkItem && drinkItem.typeId === activeTypeId) {
              matchesFilter = true;
            }
          }

          let filteredPrice: number | null = null;
          if (matchesFilter && selectedSize) {
            filteredPrice = getLocationPriceByName(selectedCoffeeLocation, drink.name, selectedSize);
          }

          legacyItems.push({
            itemName: drink.name,
            sizes: drink.price || null,
            matchesFilter,
            filteredPrice,
            format: 'hot',
          });
        }
      });
    }

    if (selectedCoffeeLocation.icedCoffeeTea && Array.isArray(selectedCoffeeLocation.icedCoffeeTea)) {
      selectedCoffeeLocation.icedCoffeeTea.forEach((drink: any) => {
        if (drink.name) {
          // Use preprocessed typeId if available
          const preprocessed = preprocessedLocations[selectedCoffeeLocation._id || selectedCoffeeLocation.name];
          let matchesFilter = false;
          if (hasActiveFilter && activeTypeId && preprocessed) {
            const drinkItem = preprocessed.coffeeHotDrinks?.find((d: any) => d.name === drink.name);
            if (drinkItem && drinkItem.typeId === activeTypeId) {
              matchesFilter = true;
            }
          }

          let filteredPrice: number | null = null;
          if (matchesFilter && selectedSize) {
            filteredPrice = getLocationPriceByName(selectedCoffeeLocation, drink.name, selectedSize);
          }

          legacyItems.push({
            itemName: drink.name,
            sizes: drink.price || null,
            matchesFilter,
            filteredPrice,
            format: 'iced',
          });
        }
      });
    }

    // Combine all items
    const allItems = [...coffeeMenuItems, ...legacyItems];

    // Sort: matching items first, then others
    const sortedItems = allItems.sort((a, b) => {
      if (a.matchesFilter && !b.matchesFilter) return -1;
      if (!a.matchesFilter && b.matchesFilter) return 1;
      return 0;
    });

    // Collect all unique milk options from all menu items
    const allMilkOptions: Record<string, number> = {};

    sortedItems.forEach(({ itemName, format }) => {
      let milkOptions: Record<string, number> | null = null;

      if (format === 'coffee_menu' && selectedCoffeeLocation.coffee_menu) {
        const menuItem = selectedCoffeeLocation.coffee_menu[itemName];
        if (menuItem && typeof menuItem === 'object' && menuItem.Milks) {
          milkOptions = menuItem.Milks;
        }
      } else if (format === 'hot' && selectedCoffeeLocation.coffeeHotDrinks) {
        const drink = selectedCoffeeLocation.coffeeHotDrinks.find((d: any) => d.name === itemName);
        if (drink && drink.milks) {
          milkOptions = drink.milks;
        }
      } else if (format === 'iced' && selectedCoffeeLocation.icedCoffeeTea) {
        const drink = selectedCoffeeLocation.icedCoffeeTea.find((d: any) => d.name === itemName);
        if (drink && drink.milks) {
          milkOptions = drink.milks;
        }
      }

      // Merge milk options, taking the maximum price if duplicate
      if (milkOptions) {
        Object.entries(milkOptions).forEach(([milk, price]) => {
          if (
            !allMilkOptions[milk] ||
            (typeof price === 'number' && typeof allMilkOptions[milk] === 'number' && price > allMilkOptions[milk])
          ) {
            allMilkOptions[milk] = price;
          }
        });
      }
    });

    // Group milk options by price (combine milks with the same price)
    const milkOptionsByPrice: Record<string, string[]> = {};
    Object.entries(allMilkOptions).forEach(([milk, price]) => {
      const priceKey = typeof price === 'number' ? price.toFixed(2) : 'free';
      if (!milkOptionsByPrice[priceKey]) {
        milkOptionsByPrice[priceKey] = [];
      }
      milkOptionsByPrice[priceKey].push(milk);
    });

    // Convert grouped milk options to display format
    const groupedMilkOptions: Array<{ milks: string[]; price: number; displayName: string }> = [];
    Object.entries(milkOptionsByPrice).forEach(([priceKey, milks]) => {
      const price = priceKey === 'free' ? 0 : parseFloat(priceKey);
      groupedMilkOptions.push({
        milks: milks.sort(), // Sort alphabetically
        price,
        displayName: milks.join(', '), // Combine names with comma
      });
    });

    // Sort by price (free first, then ascending)
    groupedMilkOptions.sort((a, b) => {
      if (a.price === 0 && b.price !== 0) return -1;
      if (a.price !== 0 && b.price === 0) return 1;
      return a.price - b.price;
    });

    return (
      <>
        {/* Coffee Menu */}
        {sortedItems.length > 0 && (
          <div>
            <div
              style={{
                fontSize: '0.875rem',
                color: 'rgba(17, 24, 39, 0.6)',
                marginBottom: '16px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Coffee Menu
            </div>
            {sortedItems
              .filter(({ itemName, sizes, matchesFilter, filteredPrice, format }) => {
                // Exclude menu items if all their prices are < $3
                if (typeof sizes === 'object' && sizes !== null) {
                  const validPrices = Object.values(sizes).filter(
                    (price: any) => typeof price === 'number' && price >= 3
                  );
                  return validPrices.length > 0;
                } else if (typeof sizes === 'number') {
                  return sizes >= 3;
                }
                // Keep items without prices (null)
                return true;
              })
              .map(({ itemName, sizes, matchesFilter, filteredPrice, format }) => {
                // Determine if this is the filtered item
                const isFilteredItem = matchesFilter && filteredPrice !== null;

                // Create unique key that includes format for legacy items
                const uniqueKey = format === 'coffee_menu' ? itemName : `${format}-${itemName}`;

                return (
                  <div key={uniqueKey} style={{ marginBottom: '12px' }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: isFilteredItem ? '16px 20px' : '12px 16px',
                        background: isFilteredItem ? 'rgba(124, 58, 237, 0.1)' : 'rgba(255, 255, 255, 0.6)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: isFilteredItem ? '2px solid rgba(124, 58, 237, 0.4)' : '1.5px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '12px',
                        boxShadow: isFilteredItem ? '0 4px 12px rgba(124, 58, 237, 0.15)' : 'none',
                        transition: 'all 0.2s',
                      }}
                    >
                      <div>
                        <div
                          style={{
                            fontSize: isFilteredItem ? '1rem' : '0.9375rem',
                            color: '#111827',
                            fontWeight: isFilteredItem ? 600 : 500,
                            marginBottom: '4px',
                          }}
                        >
                          {itemName}
                        </div>
                      </div>
                      <div
                        style={{
                          textAlign: 'right',
                          marginLeft: '12px',
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '4px',
                        }}
                      >
                        {typeof sizes === 'object' && sizes !== null ? (
                          Object.entries(sizes)
                            .filter(([sizeName, price]: [string, any]) => {
                              // Exclude prices < $3
                              return typeof price === 'number' ? price >= 3 : true;
                            })
                            .map(([sizeName, price]: [string, any]) => {
                              // Check if this is the filtered size
                              const isFilteredSize =
                                isFilteredItem && selectedSize && sizeName.toLowerCase() === selectedSize.toLowerCase();

                              // Capitalize size name for display
                              const displaySizeName = sizeName.charAt(0).toUpperCase() + sizeName.slice(1);

                              return (
                                <div
                                  key={sizeName}
                                  style={{
                                    fontSize: isFilteredSize ? '1.125rem' : '0.875rem',
                                    color: isFilteredSize ? '#7c3aed' : '#111827',
                                    fontWeight: isFilteredSize ? 700 : 500,
                                    textShadow: isFilteredSize ? '0 1px 2px rgba(124, 58, 237, 0.2)' : 'none',
                                  }}
                                >
                                  {displaySizeName}: ${typeof price === 'number' ? price.toFixed(2) : price}
                                </div>
                              );
                            })
                        ) : sizes !== null && (typeof sizes !== 'number' || sizes >= 3) ? (
                          <div
                            style={{
                              fontSize: isFilteredItem ? '1.125rem' : '0.875rem',
                              color: isFilteredItem ? '#7c3aed' : '#111827',
                              fontWeight: isFilteredItem ? 700 : 500,
                              textShadow: isFilteredItem ? '0 1px 2px rgba(124, 58, 237, 0.2)' : 'none',
                            }}
                          >
                            ${typeof sizes === 'number' ? sizes.toFixed(2) : sizes}
                          </div>
                        ) : (
                          <div
                            style={{
                              fontSize: '0.875rem',
                              color: 'rgba(17, 24, 39, 0.5)',
                              fontStyle: 'italic',
                            }}
                          >
                            No price available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>
        )}

        {/* Centralized Milk Options */}
        {groupedMilkOptions.length > 0 && (
          <div style={{ marginTop: '24px' }}>
            <div
              style={{
                fontSize: '0.875rem',
                color: 'rgba(17, 24, 39, 0.6)',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Milk Options
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '4px',
              }}
            >
              {groupedMilkOptions.map(({ milks, price, displayName }) => {
                // Check if any of the milks in this group matches the selected milk filter
                const isSelectedMilk =
                  selectedMilk && milks.some((milk) => milk.toLowerCase() === selectedMilk.toLowerCase());

                return (
                  <div
                    key={displayName}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      background: isSelectedMilk ? 'rgba(124, 58, 237, 0.15)' : 'rgba(0, 0, 0, 0.03)',
                      border: isSelectedMilk ? '1.5px solid rgba(124, 58, 237, 0.4)' : 'none',
                      borderRadius: '6px',
                      transition: 'all 0.2s',
                      overflow: 'hidden',
                    }}
                  >
                    {/* Header: Price */}
                    <div
                      style={{
                        padding: '6px 8px',
                        background: isSelectedMilk ? 'rgba(124, 58, 237, 0.2)' : 'rgba(0, 0, 0, 0.05)',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.05)',
                      }}
                    >
                      <span
                        style={{
                          color: isSelectedMilk ? '#7c3aed' : '#6B7280',
                          fontWeight: 600,
                          fontSize: '0.8125rem',
                        }}
                      >
                        {price > 0 ? `+$${price.toFixed(2)}` : 'Free'}
                      </span>
                    </div>
                    {/* Body: Milk Options */}
                    <div
                      style={{
                        padding: '6px 8px',
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '4px',
                      }}
                    >
                      {milks.map((milk, index) => (
                        <span
                          key={index}
                          style={{
                            color: isSelectedMilk ? '#7c3aed' : '#374151',
                            fontWeight: isSelectedMilk ? 500 : 400,
                            fontSize: '0.75rem',
                          }}
                        >
                          {milk}
                          {index < milks.length - 1 ? ',' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Opening Hours */}
        {(() => {
          const hours =
            selectedCoffeeLocation.openingHours ||
            selectedCoffeeLocation.business_hours ||
            selectedCoffeeLocation.opening_hours;
          if (hours) {
            // console.log('🕐 Opening hours found in modal:', hours);
          } else {
            // console.log('❌ No opening hours found. Available keys:', Object.keys(selectedCoffeeLocation));
          }
          return hours;
        })() && (
          <div style={{ marginTop: '24px' }}>
            <div
              style={{
                fontSize: '0.875rem',
                color: 'rgba(17, 24, 39, 0.6)',
                marginBottom: '12px',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Opening Hours
            </div>
            <div
              style={{
                padding: '12px 16px',
                background: 'rgba(255, 255, 255, 0.6)',
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: '1.5px solid rgba(0, 0, 0, 0.1)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
              }}
            >
              <Clock size={18} style={{ color: 'rgba(17, 24, 39, 0.6)', marginTop: '2px', flexShrink: 0 }} />
              <div
                style={{
                  fontSize: '0.9375rem',
                  color: '#111827',
                  whiteSpace: 'pre-line',
                  lineHeight: '1.6',
                }}
              >
                {formatOpeningHours(
                  selectedCoffeeLocation.openingHours ||
                    selectedCoffeeLocation.business_hours ||
                    selectedCoffeeLocation.opening_hours
                )}
              </div>
            </div>
          </div>
        )}

        {/* Get Directions Button */}
        {selectedCoffeeLocation.latitude && selectedCoffeeLocation.longitude && (
          <div style={{ marginTop: '24px' }}>
            <button
              className="directions-button"
              onClick={() => {
                const lat = selectedCoffeeLocation.latitude;
                const lon = selectedCoffeeLocation.longitude;
                if (lat && lon) {
                  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lon}`;
                  // console.log('🗺️ Opening Google Maps with URL:', googleMapsUrl);
                  window.open(googleMapsUrl, '_blank');
                } else {
                  console.error('❌ No coordinates available for directions');
                  alert('Location coordinates not available for directions');
                }
              }}
              title="Open Google Maps with directions to this location"
              style={{
                width: '100%',
                marginTop: '0',
              }}
            >
              <Navigation size={18} />
              <span>Get Directions</span>
            </button>
          </div>
        )}

        {/* Website Link */}
        {selectedCoffeeLocation.website && (
          <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(0, 0, 0, 0.1)' }}>
            <a
              href={selectedCoffeeLocation.website}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-block',
                padding: '12px 24px',
                background: 'rgba(124, 58, 237, 0.1)',
                color: '#7c3aed',
                borderRadius: '12px',
                fontWeight: 500,
                textDecoration: 'none',
                transition: 'all 0.2s',
              }}
            >
              Visit Website
            </a>
          </div>
        )}
      </>
    );
  };

  const tabsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Create header configuration for AppHeader
  const headerConfig: HeaderConfig = useMemo(
    () => ({
      left: (
        <button onClick={() => setShowCoffeeList(true)} className="icon-action-button" title="View list">
          <List size={18} />
        </button>
      ),
      center: (
        <button onClick={() => handleFilterClick('coffee')} className="coffee-type-selector-button">
          <span>{activeTypeId ? getTypeLabel(activeTypeId) : 'All Cafes'}</span>
          <ChevronDown size={18} />
        </button>
      ),
      right: (
        <>
          <button
            onClick={() => setShowCoffeeSearch(true)}
            className="icon-action-button"
            title="Search cafes and coffee types"
          >
            <Search size={18} />
          </button>
          {onClose && (
            <button onClick={onClose} className="icon-action-button close" title="Close">
              <X size={20} />
            </button>
          )}
        </>
      ),
      backgroundColor: '#7c3aed',
      variant: 'default',
    }),
    [activeTypeId, getTypeLabel, handleFilterClick, setShowCoffeeList, setShowCoffeeSearch, onClose]
  );

  // Measure tabs height for content positioning (header is now handled by AppShell)
  useEffect(() => {
    const updateHeights = () => {
      if (tabsRef.current && containerRef.current) {
        const tabsHeight = tabsRef.current.offsetHeight;
        containerRef.current.style.setProperty('--coffee-tabs-height', `${tabsHeight}px`);
      }
    };

    updateHeights();
    window.addEventListener('resize', updateHeights);
    return () => window.removeEventListener('resize', updateHeights);
  }, []);

  // Ensure horizontal scrolling works on tabs container
  useEffect(() => {
    const tabsContainer = tabsRef.current;
    if (!tabsContainer) return;

    // Ensure scrolling is enabled
    const handleTouchStart = (e: TouchEvent) => {
      // Allow default scrolling behavior
    };

    const handleTouchMove = (e: TouchEvent) => {
      // Allow default scrolling behavior
    };

    tabsContainer.addEventListener('touchstart', handleTouchStart, { passive: true });
    tabsContainer.addEventListener('touchmove', handleTouchMove, { passive: true });

    return () => {
      tabsContainer.removeEventListener('touchstart', handleTouchStart);
      tabsContainer.removeEventListener('touchmove', handleTouchMove);
    };
  }, []);

  return (
    <AppShell
      headerConfig={headerConfig}
      showFooter={!hideNavbar}
      activeTab={activeTab}
      onTabChange={onTabChange}
      onMyVibeClick={onMyVibeClick}
    >
      <div ref={containerRef} className="coffee-mapper-container">
        {/* Navigation Tabs */}
        <div ref={tabsRef} className="coffee-mapper-tabs">
          <button
            onClick={() => handleFilterClick('size')}
            className={`tab-button ${activeFilter === 'size' ? 'active' : ''}`}
          >
            <Coffee size={18} />
            <span>Size: {selectedSize === 'small' ? 'Small' : selectedSize === 'regular' ? 'Regular' : 'Large'}</span>
          </button>
          <button onClick={handlePopularToggle} className={`tab-button ${showPopularOnly ? 'active' : ''}`}>
            <Star size={18} />
            <span>Popular</span>
          </button>
          <button
            onClick={() => handleFilterClick('price')}
            className={`tab-button ${priceRange !== null ? 'active' : ''}`}
          >
            <DollarSign size={18} />
            <span>Price</span>
          </button>
          <button
            onClick={() => handleFilterClick('rating')}
            className={`tab-button ${minRating !== null ? 'active' : ''}`}
          >
            <Star size={18} />
            <span>Rating</span>
          </button>
          <button
            onClick={() => setActiveFilter('milk')}
            className={`tab-button ${activeFilter === 'milk' ? 'active' : ''} ${selectedMilk ? 'has-selection' : ''}`}
          >
            <Milk size={18} />
            <span>{selectedMilk || 'Milk'}</span>
          </button>
          <button onClick={() => setShowCoffeeList(true)} className={`tab-button ${showCoffeeList ? 'active' : ''}`}>
            <List size={18} />
            <span>List</span>
          </button>
          {hasActiveFilters && (
            <button onClick={handleResetAllFilters} className="tab-button clear-filter-button">
              <X size={18} />
              <span>Clear</span>
            </button>
          )}
        </div>

        {/* Pin Display Toggle - Glassmorphic Toggle in Bottom Center */}
        <div
          style={{
            position: 'fixed',
            bottom: 'calc(80px + env(safe-area-inset-bottom, 0px))',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
            display: 'flex',
            gap: '4px',
            padding: '4px',
            marginBottom: '6px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(20px) saturate(180%)',
            WebkitBackdropFilter: 'blur(20px) saturate(180%)',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            borderRadius: '20px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
          }}
        >
          <button
            onClick={() => setPinDisplayMode('price')}
            style={{
              padding: '4px 10px',
              background: pinDisplayMode === 'price' ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
              border: 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.7rem',
              fontWeight: pinDisplayMode === 'price' ? 600 : 500,
              color: pinDisplayMode === 'price' ? '#7c3aed' : '#111827',
            }}
            onMouseEnter={(e) => {
              if (pinDisplayMode !== 'price') {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (pinDisplayMode !== 'price') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <DollarSign size={12} />
            <span>Price</span>
          </button>
          <button
            onClick={() => setPinDisplayMode('rating')}
            style={{
              padding: '4px 10px',
              background: pinDisplayMode === 'rating' ? 'rgba(124, 58, 237, 0.15)' : 'transparent',
              border: 'none',
              borderRadius: '16px',
              cursor: 'pointer',
              transition: 'all 0.2s',
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              fontSize: '0.7rem',
              fontWeight: pinDisplayMode === 'rating' ? 600 : 500,
              color: pinDisplayMode === 'rating' ? '#7c3aed' : '#111827',
            }}
            onMouseEnter={(e) => {
              if (pinDisplayMode !== 'rating') {
                e.currentTarget.style.background = 'rgba(0, 0, 0, 0.05)';
              }
            }}
            onMouseLeave={(e) => {
              if (pinDisplayMode !== 'rating') {
                e.currentTarget.style.background = 'transparent';
              }
            }}
          >
            <Star
              size={12}
              fill={pinDisplayMode === 'rating' ? '#7c3aed' : 'none'}
              color={pinDisplayMode === 'rating' ? '#7c3aed' : '#111827'}
            />
            <span>Rating</span>
          </button>
        </div>

        <main className="map-container coffee-mapper-map">
          <MapContainer
            center={mapCenter}
            zoom={mapZoom}
            zoomControl={false}
            className="map-container"
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
              subdomains="abcd"
              maxZoom={20}
            />
            <MarkerClusterGroup
              chunkedLoading={true}
              chunkInterval={150}
              chunkDelay={100}
              removeOutsideVisibleBounds={true}
              disableClusteringAtZoom={15}
              maxClusterRadius={50}
              spiderfyOnMaxZoom={true}
              showCoverageOnHover={false}
              zoomToBoundsOnClick={true}
              animate={false}
              animateAddingMarkers={false}
            >
              {coffeeMarkers}
            </MarkerClusterGroup>

            {/* User Location Marker */}
            {userLocationMarker}

            {/* MapController to set mapRef */}
            <MapController mapRef={mapRef} />

            {/* Zoom change handler */}
            <ZoomHandler onZoomChange={setCurrentZoom} />

            {/* Current Location Button */}
            <div className="current-location-button-container">
              <button
                onClick={centerOnCurrentLocation}
                disabled={isLocating}
                className="current-location-button"
                title="Center on my location"
              >
                {isLocating ? (
                  <div className="location-spinner"></div>
                ) : (
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
                    <circle cx="12" cy="9" r="2.5" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => setShowHelpModal(true)}
                className="current-location-button help-button"
                title="How to use Coffee Mapper"
                style={{ marginTop: '8px' }}
              >
                <HelpCircle size={20} />
              </button>
            </div>
          </MapContainer>
        </main>
        {/* Filter Modal - Portal to document.body for proper z-index */}
        {createPortal(
          <AnimatePresence>
            {activeFilter && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="coffee-filter-modal-overlay"
                onClick={handleCancelFilter}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="coffee-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="coffee-filter-modal-header">
                    <h2>
                      {activeFilter === 'coffee'
                        ? 'Coffee Type'
                        : activeFilter === 'size'
                          ? 'Size'
                          : activeFilter === 'milk'
                            ? 'Milk Options'
                            : 'Price'}
                    </h2>
                    {(activeFilter === 'coffee' && activeTypeId !== null) ||
                    (activeFilter === 'milk' && selectedMilk !== null) ||
                    (activeFilter === 'price' && priceRange !== null) ? (
                      <button onClick={handleClearFilter} className="coffee-filter-clear-button">
                        Clear {activeFilter === 'coffee' ? 'coffee' : activeFilter === 'milk' ? 'milk' : 'price'}
                      </button>
                    ) : null}
                  </div>
                  <div className="coffee-filter-modal-content">
                    {activeFilter === 'coffee' ? (
                      <div className="coffee-type-filter">
                        {/* Level 1: Mode Switcher - Always visible at top */}
                        <div
                          style={{
                            fontSize: '0.875rem',
                            color: 'rgba(17, 24, 39, 0.6)',
                            marginBottom: '8px',
                            textAlign: 'center',
                            fontWeight: 500,
                          }}
                        >
                          Choose a filter method
                        </div>
                        <div
                          className="coffee-filter-mode-switcher"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '12px',
                            marginBottom: '12px',
                            width: '100%',
                            position: 'relative',
                            zIndex: 1000,
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => handleModeChange('popular')}
                            className={`coffee-filter-mode-button ${mode === 'popular' ? 'active' : ''}`}
                            style={{
                              padding: '14px 20px',
                              background: mode === 'popular' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                              border: `1.5px solid ${mode === 'popular' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                              borderRadius: '12px',
                              color: mode === 'popular' ? '#7c3aed' : '#111827',
                              fontSize: '0.9375rem',
                              fontWeight: mode === 'popular' ? 600 : 500,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <span>Popular</span>
                            {mode === 'popular' && <Star size={16} style={{ color: '#7c3aed' }} />}
                          </button>
                          <button
                            type="button"
                            onClick={() => handleModeChange('categories')}
                            className={`coffee-filter-mode-button ${mode === 'categories' ? 'active' : ''}`}
                            style={{
                              padding: '14px 20px',
                              background:
                                mode === 'categories' ? 'rgba(124, 58, 237, 0.15)' : 'rgba(255, 255, 255, 0.6)',
                              border: `1.5px solid ${mode === 'categories' ? '#7c3aed' : 'rgba(0, 0, 0, 0.1)'}`,
                              borderRadius: '12px',
                              color: mode === 'categories' ? '#7c3aed' : '#111827',
                              fontSize: '0.9375rem',
                              fontWeight: mode === 'categories' ? 600 : 500,
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '6px',
                              transition: 'all 0.2s ease',
                            }}
                          >
                            <span>Categories</span>
                            {mode === 'categories' && <List size={16} style={{ color: '#7c3aed' }} />}
                          </button>
                        </div>

                        {/* Level 2: Temperature Selection (only in categories mode, when no temperature selected) */}
                        {mode === 'categories' && temperatureFilter === null && (
                          <div className="coffee-filter-temperature">
                            <div
                              style={{
                                fontSize: '0.875rem',
                                color: 'rgba(17, 24, 39, 0.6)',
                                marginBottom: '16px',
                                textAlign: 'center',
                                fontWeight: 500,
                              }}
                            >
                              Select a drink type to continue
                            </div>
                            <div className="coffee-filter-grid" style={{ gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                              <button
                                onClick={() => handleTemperatureSelect('hot')}
                                className="coffee-filter-card coffee-filter-temperature-card"
                                style={{
                                  padding: '24px 20px',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <span className="coffee-filter-card-name">Coffee / Hot Drinks</span>
                                <ChevronDown
                                  size={20}
                                  style={{
                                    color: 'rgba(17, 24, 39, 0.4)',
                                    transform: 'rotate(-90deg)',
                                    marginTop: '4px',
                                  }}
                                />
                              </button>
                              <button
                                onClick={() => handleTemperatureSelect('cold')}
                                className="coffee-filter-card coffee-filter-temperature-card"
                                style={{
                                  padding: '24px 20px',
                                  fontSize: '1rem',
                                  fontWeight: 600,
                                  position: 'relative',
                                  display: 'flex',
                                  flexDirection: 'column',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <span className="coffee-filter-card-name">Cold / Iced Drinks</span>
                                <ChevronDown
                                  size={20}
                                  style={{
                                    color: 'rgba(17, 24, 39, 0.4)',
                                    transform: 'rotate(-90deg)',
                                    marginTop: '4px',
                                  }}
                                />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Level 3: Categories (only for hot drinks in categories mode, when temperature is selected) */}
                        {mode === 'categories' && temperatureFilter === 'hot' && !activeCategoryId && (
                          <div className="coffee-filter-categories">
                            <button
                              onClick={() => setTemperatureFilter(null)}
                              className="coffee-filter-back-button"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 16px',
                                background: 'rgba(0, 0, 0, 0.05)',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '0.9375rem',
                                fontWeight: 500,
                                color: '#111827',
                                marginBottom: '12px',
                              }}
                            >
                              <ArrowLeft size={18} />
                              Back
                            </button>
                            <div
                              style={{
                                fontSize: '0.875rem',
                                color: 'rgba(17, 24, 39, 0.6)',
                                marginBottom: '12px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              Coffee / Hot Drinks
                            </div>
                            <div className="coffee-filter-grid">
                              {getCategoriesByTemperature('hot').map((category) => (
                                <button
                                  key={category.id}
                                  onClick={() => handleCategorySelect(category.id)}
                                  className="coffee-filter-card"
                                >
                                  <span className="coffee-filter-card-name">{category.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Level 3: Cold drink types (directly, skip categories for cold drinks) */}
                        {mode === 'categories' && temperatureFilter === 'cold' && !activeCategoryId && (
                          <div className="coffee-filter-types">
                            <button
                              onClick={() => setTemperatureFilter(null)}
                              className="coffee-filter-back-button"
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '12px 16px',
                                background: 'rgba(0, 0, 0, 0.05)',
                                border: 'none',
                                borderRadius: '12px',
                                cursor: 'pointer',
                                fontSize: '0.9375rem',
                                fontWeight: 500,
                                color: '#111827',
                                marginBottom: '12px',
                              }}
                            >
                              <ArrowLeft size={18} />
                              Back
                            </button>
                            <div
                              style={{
                                fontSize: '0.875rem',
                                color: 'rgba(17, 24, 39, 0.6)',
                                marginBottom: '10px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                              }}
                            >
                              Cold / Iced Drinks
                              {!activeTypeId && (
                                <span
                                  style={{
                                    fontSize: '0.8125rem',
                                    color: 'rgba(17, 24, 39, 0.5)',
                                    textTransform: 'none',
                                    letterSpacing: 'normal',
                                    fontWeight: 400,
                                    marginLeft: '8px',
                                  }}
                                >
                                  • Choose a coffee type
                                </span>
                              )}
                            </div>
                            <div className="coffee-filter-grid">
                              {getAllColdTypes().map((type) => (
                                <button
                                  key={type.id}
                                  onClick={() => handleTypeSelect(type.id)}
                                  className={`coffee-filter-card ${activeTypeId === type.id ? 'selected' : ''}`}
                                >
                                  <span className="coffee-filter-card-name">{type.label}</span>
                                </button>
                              ))}
                            </div>
                          </div>
                        )}

                        {/* Back button when category is selected */}
                        {mode === 'categories' && activeCategoryId && (
                          <button
                            onClick={() => setActiveCategoryId(null)}
                            className="coffee-filter-back-button"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              padding: '12px 16px',
                              background: 'rgba(0, 0, 0, 0.05)',
                              border: 'none',
                              borderRadius: '12px',
                              cursor: 'pointer',
                              fontSize: '0.9375rem',
                              fontWeight: 500,
                              color: '#111827',
                              marginBottom: '12px',
                            }}
                          >
                            <ArrowLeft size={18} />
                            Back to Categories
                          </button>
                        )}

                        {/* Level 4: Types (popular mode or after category selected for hot drinks) */}
                        {(mode === 'popular' ||
                          (mode === 'categories' && temperatureFilter === 'hot' && activeCategoryId)) && (
                          <div className="coffee-filter-types">
                            {mode === 'popular' && !activeTypeId && (
                              <div
                                style={{
                                  fontSize: '0.8125rem',
                                  color: 'rgba(17, 24, 39, 0.5)',
                                  marginBottom: '10px',
                                  textAlign: 'center',
                                  fontWeight: 400,
                                }}
                              >
                                Choose a coffee type
                              </div>
                            )}
                            {mode === 'categories' && activeCategoryId && (
                              <div
                                style={{
                                  fontSize: '0.875rem',
                                  color: 'rgba(17, 24, 39, 0.6)',
                                  marginBottom: activeTypeId ? '12px' : '10px',
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.5px',
                                }}
                              >
                                {getCategoryLabel(activeCategoryId)}
                                {!activeTypeId && (
                                  <span
                                    style={{
                                      fontSize: '0.8125rem',
                                      color: 'rgba(17, 24, 39, 0.5)',
                                      textTransform: 'none',
                                      letterSpacing: 'normal',
                                      fontWeight: 400,
                                      marginLeft: '8px',
                                    }}
                                  >
                                    • Choose a coffee type
                                  </span>
                                )}
                              </div>
                            )}
                            <div className="coffee-filter-grid">
                              {mode === 'popular'
                                ? getPopularTypeIds().map((typeId) => (
                                    <button
                                      key={typeId}
                                      onClick={() => handleTypeSelect(typeId)}
                                      className={`coffee-filter-card ${activeTypeId === typeId ? 'selected' : ''}`}
                                    >
                                      <span className="coffee-filter-card-name">{getTypeLabel(typeId)}</span>
                                    </button>
                                  ))
                                : activeCategoryId &&
                                  getTypesForCategory(activeCategoryId).map((type) => (
                                    <button
                                      key={type.id}
                                      onClick={() => handleTypeSelect(type.id)}
                                      className={`coffee-filter-card ${activeTypeId === type.id ? 'selected' : ''}`}
                                    >
                                      <span className="coffee-filter-card-name">{type.label}</span>
                                    </button>
                                  ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : activeFilter === 'size' ? (
                      <div className="size-filter">
                        <div className="coffee-filter-list">
                          <div className="coffee-filter-item">
                            <span className="coffee-filter-item-name">Small</span>
                            <button
                              onClick={() => handleSizeToggle('small')}
                              className={`coffee-filter-toggle ${selectedSize === 'small' ? 'active' : ''}`}
                            >
                              <div className="coffee-filter-toggle-slider" />
                            </button>
                          </div>
                          <div className="coffee-filter-item">
                            <span className="coffee-filter-item-name">Regular</span>
                            <button
                              onClick={() => handleSizeToggle('regular')}
                              className={`coffee-filter-toggle ${selectedSize === 'regular' ? 'active' : ''}`}
                            >
                              <div className="coffee-filter-toggle-slider" />
                            </button>
                          </div>
                          <div className="coffee-filter-item">
                            <span className="coffee-filter-item-name">Large</span>
                            <button
                              onClick={() => handleSizeToggle('large')}
                              className={`coffee-filter-toggle ${selectedSize === 'large' ? 'active' : ''}`}
                            >
                              <div className="coffee-filter-toggle-slider" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : activeFilter === 'milk' ? (
                      <div className="milk-filter">
                        <div className="coffee-filter-list">
                          <div className="coffee-filter-item">
                            <span className="coffee-filter-item-name">All Milks</span>
                            <button
                              onClick={() => handleMilkSelect(null)}
                              className={`coffee-filter-toggle ${!selectedMilk ? 'active' : ''}`}
                            >
                              <div className="coffee-filter-toggle-slider" />
                            </button>
                          </div>
                          {MILK_OPTIONS.map((milk) => (
                            <div key={milk} className="coffee-filter-item">
                              <span className="coffee-filter-item-name">{milk}</span>
                              <button
                                onClick={() => handleMilkSelect(milk)}
                                className={`coffee-filter-toggle ${selectedMilk === milk ? 'active' : ''}`}
                              >
                                <div className="coffee-filter-toggle-slider" />
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : activeFilter === 'price' ? (
                      <div className="price-filter">
                        <div className="price-preset-grid">
                          <button
                            onClick={() => handlePresetPriceRange(null)}
                            className={`price-preset-button ${!priceRange ? 'selected' : ''}`}
                          >
                            All Prices
                          </button>
                          <button
                            onClick={() => handlePresetPriceRange({ min: SLIDER_PRICE_MIN, max: 4.5 })}
                            className={`price-preset-button ${priceRange?.min === SLIDER_PRICE_MIN && priceRange?.max === 4.5 ? 'selected' : ''}`}
                          >
                            Under $4.50
                          </button>
                          <button
                            onClick={() => handlePresetPriceRange({ min: 4.5, max: 5.5 })}
                            className={`price-preset-button ${priceRange?.min === 4.5 && priceRange?.max === 5.5 ? 'selected' : ''}`}
                          >
                            $4.50 - $5.50
                          </button>
                          <button
                            onClick={() => handlePresetPriceRange({ min: 5.5, max: 6.5 })}
                            className={`price-preset-button ${priceRange?.min === 5.5 && priceRange?.max === 6.5 ? 'selected' : ''}`}
                          >
                            $5.50 - $6.50
                          </button>
                          <button
                            onClick={() => handlePresetPriceRange({ min: 6.5, max: SLIDER_PRICE_MAX })}
                            className={`price-preset-button ${priceRange?.min === 6.5 && priceRange?.max === SLIDER_PRICE_MAX ? 'selected' : ''}`}
                          >
                            $6.50+
                          </button>
                        </div>

                        {/* Range Slider */}
                        <div className="price-slider-container" style={{ marginTop: '24px' }}>
                          <div className="price-slider-header">
                            <span className="price-slider-label">
                              ${sliderMin.toFixed(2)} - ${sliderMax.toFixed(2)}
                            </span>
                          </div>
                          <div className="price-slider-wrapper">
                            <div className="price-slider-track">
                              <div
                                className="price-slider-range"
                                style={{
                                  left: `${((sliderMin - SLIDER_PRICE_MIN) / (SLIDER_PRICE_MAX - SLIDER_PRICE_MIN)) * 100}%`,
                                  width: `${((sliderMax - sliderMin) / (SLIDER_PRICE_MAX - SLIDER_PRICE_MIN)) * 100}%`,
                                }}
                              />
                              <input
                                type="range"
                                min={SLIDER_PRICE_MIN}
                                max={SLIDER_PRICE_MAX}
                                step={0.1}
                                value={sliderMin}
                                onChange={(e) => handleSliderChange('min', parseFloat(e.target.value))}
                                className="price-slider-input price-slider-input-min"
                              />
                              <input
                                type="range"
                                min={SLIDER_PRICE_MIN}
                                max={SLIDER_PRICE_MAX}
                                step={0.1}
                                value={sliderMax}
                                onChange={(e) => handleSliderChange('max', parseFloat(e.target.value))}
                                className="price-slider-input price-slider-input-max"
                              />
                            </div>
                            <div className="price-slider-labels">
                              <span className="price-slider-min-label">${SLIDER_PRICE_MIN.toFixed(2)}</span>
                              <span className="price-slider-max-label">${SLIDER_PRICE_MAX.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : activeFilter === 'rating' ? (
                      <div className="rating-filter">
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '8px' }}>
                            <span style={{ fontWeight: 600, color: '#111827', fontSize: '0.9375rem' }}>Rating</span>
                            <span style={{ color: 'rgba(17, 24, 39, 0.6)', fontSize: '0.875rem' }}>•</span>
                            <span style={{ color: 'rgba(17, 24, 39, 0.6)', fontSize: '0.875rem' }}>at least</span>
                          </div>
                        </div>
                        <div
                          className="price-preset-grid"
                          style={{
                            display: 'flex',
                            gap: '0',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            borderRadius: '8px',
                            overflow: 'hidden',
                          }}
                        >
                          <button
                            onClick={() => handleRatingSelect(null)}
                            className={`price-preset-button ${!minRating ? 'selected' : ''}`}
                            style={{
                              flex: 1,
                              borderRadius: 0,
                              border: 'none',
                              borderRight: minRating ? '1px solid rgba(0, 0, 0, 0.1)' : 'none',
                            }}
                          >
                            Any
                          </button>
                          <button
                            onClick={() => handleRatingSelect(3.5)}
                            className={`price-preset-button ${minRating === 3.5 ? 'selected' : ''}`}
                            style={{
                              flex: 1,
                              borderRadius: 0,
                              border: 'none',
                              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}
                          >
                            3.5<span style={{ color: '#FFA500', fontSize: '0.875rem' }}>★</span>
                          </button>
                          <button
                            onClick={() => handleRatingSelect(4.0)}
                            className={`price-preset-button ${minRating === 4.0 ? 'selected' : ''}`}
                            style={{
                              flex: 1,
                              borderRadius: 0,
                              border: 'none',
                              borderRight: '1px solid rgba(0, 0, 0, 0.1)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}
                          >
                            4.0<span style={{ color: '#FFA500', fontSize: '0.875rem' }}>★</span>
                          </button>
                          <button
                            onClick={() => handleRatingSelect(4.5)}
                            className={`price-preset-button ${minRating === 4.5 ? 'selected' : ''}`}
                            style={{
                              flex: 1,
                              borderRadius: 0,
                              border: 'none',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              gap: '4px',
                            }}
                          >
                            4.5<span style={{ color: '#FFA500', fontSize: '0.875rem' }}>★</span>
                          </button>
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div className="coffee-filter-modal-footer">
                    <button onClick={handleCancelFilter} className="coffee-filter-button cancel">
                      Cancel
                    </button>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Coffee Location Detail Modal - Portal to document.body */}
        {createPortal(
          <AnimatePresence>
            {selectedCoffeeLocation && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="coffee-filter-modal-overlay"
                onClick={() => setSelectedCoffeeLocation(null)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="coffee-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="coffee-filter-modal-header">
                    <div style={{ flex: 1 }}>
                      <h2 style={{ margin: 0, marginBottom: '4px' }}>
                        {selectedCoffeeLocation._id || selectedCoffeeLocation.name || 'Coffee Location'}
                      </h2>
                      {selectedCoffeeLocation.review_rating && (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '0.8125rem',
                            color: 'rgba(17, 24, 39, 0.7)',
                            marginTop: '2px',
                          }}
                        >
                          <span style={{ fontWeight: 500, color: '#111827' }}>
                            {typeof selectedCoffeeLocation.review_rating === 'number'
                              ? selectedCoffeeLocation.review_rating.toFixed(1)
                              : selectedCoffeeLocation.review_rating}
                          </span>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1px' }}>
                            {Array.from({ length: 5 }, (_, i) => {
                              const rating =
                                typeof selectedCoffeeLocation.review_rating === 'number'
                                  ? selectedCoffeeLocation.review_rating
                                  : parseFloat(String(selectedCoffeeLocation.review_rating)) || 0;
                              const starValue = i + 1;
                              const filled = starValue <= rating;
                              const halfFilled = !filled && starValue - 0.5 <= rating;

                              return (
                                <div
                                  key={i}
                                  style={{ position: 'relative', width: '12px', height: '12px', flexShrink: 0 }}
                                >
                                  {/* Empty star background */}
                                  <Star
                                    size={12}
                                    fill="none"
                                    stroke="#ddd"
                                    strokeWidth={1.5}
                                    style={{ position: 'absolute', top: 0, left: 0 }}
                                  />
                                  {/* Filled star */}
                                  {filled && (
                                    <Star
                                      size={12}
                                      fill="#FFA500"
                                      color="#FFA500"
                                      style={{ position: 'absolute', top: 0, left: 0 }}
                                    />
                                  )}
                                  {/* Half-filled star */}
                                  {halfFilled && (
                                    <div
                                      style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        width: '6px',
                                        height: '12px',
                                        overflow: 'hidden',
                                      }}
                                    >
                                      <Star
                                        size={12}
                                        fill="#FFA500"
                                        color="#FFA500"
                                        style={{ position: 'absolute', top: 0, left: 0 }}
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </div>
                    <button onClick={() => setSelectedCoffeeLocation(null)} className="coffee-filter-clear-button">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="coffee-filter-modal-content">{renderCoffeeLocationModalContent()}</div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Help Modal - Portal to document.body */}
        {createPortal(
          <AnimatePresence>
            {showHelpModal && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="coffee-filter-modal-overlay"
                onClick={() => setShowHelpModal(false)}
              >
                <motion.div
                  initial={{ y: '100%' }}
                  animate={{ y: 0 }}
                  exit={{ y: '100%' }}
                  transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                  className="coffee-filter-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="coffee-filter-modal-header">
                    <h2>How to Use Coffee Mapper</h2>
                    <button onClick={() => setShowHelpModal(false)} className="coffee-filter-clear-button">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="coffee-filter-modal-content" style={{ padding: '24px 20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                      {/* Header Section */}
                      <div
                        style={{
                          background: 'rgba(124, 58, 237, 0.05)',
                          borderRadius: '16px',
                          padding: '20px',
                          border: '1px solid rgba(124, 58, 237, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '12px',
                              background: 'rgba(124, 58, 237, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Coffee size={20} color="#7c3aed" />
                          </div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>Header</h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '12px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              fontSize: '0.9375rem',
                              color: '#111827',
                              lineHeight: '1.6',
                            }}
                          >
                            <div style={{ marginBottom: '8px', fontWeight: 600 }}>Coffee Type Selector (Center)</div>
                            <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                              Tap the coffee type in the header center to open the filter modal. Choose from{' '}
                              <strong>Popular</strong> types (quick access to common drinks) or browse by{' '}
                              <strong>Categories</strong>. In Categories mode, first select{' '}
                              <strong>Hot / Cold drinks</strong>, then choose a category and specific drink type. The
                              modal automatically closes when you make a selection.
                            </div>
                          </div>
                          <div
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '12px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              fontSize: '0.9375rem',
                              color: '#111827',
                              lineHeight: '1.6',
                            }}
                          >
                            <div style={{ marginBottom: '8px', fontWeight: 600 }}>List & Search Icons</div>
                            <div style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}>
                              <strong>Left icon:</strong> View all cafes in a scrollable list with details and prices.
                              <br />
                              <strong>Right icon:</strong> Search for cafes by name or browse by category.
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Filter Tabs Section */}
                      <div
                        style={{
                          background: 'rgba(124, 58, 237, 0.05)',
                          borderRadius: '16px',
                          padding: '20px',
                          border: '1px solid rgba(124, 58, 237, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '12px',
                              background: 'rgba(124, 58, 237, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Coffee size={20} color="#7c3aed" />
                          </div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                            Filter Tabs
                          </h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {[
                            {
                              icon: Coffee,
                              title: 'Size',
                              desc: 'Filter by cup size. Displays as "Size: Regular" (or Small/Large) with a coffee icon. Default is Regular and cannot be cleared.',
                            },
                            {
                              icon: Star,
                              title: 'Popular',
                              desc: 'Show only popular coffee shops. Toggle on/off. When active, shows a purple border.',
                            },
                            {
                              icon: DollarSign,
                              title: 'Price',
                              desc: 'Filter by price range. Select a preset range or set custom limits. Shows a border when active. The modal automatically closes when you select a range.',
                            },
                            {
                              icon: List,
                              title: 'List',
                              desc: 'View all cafes in a scrollable list with details, prices, and distance from your location.',
                            },
                            {
                              icon: X,
                              title: 'Clear',
                              desc: 'Reset all active filters. Only appears when filters are active. Has a purple highlight to make it easy to identify.',
                            },
                          ].map((item, idx) => (
                            <div
                              key={idx}
                              style={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: '12px',
                                padding: '12px',
                                background: 'rgba(255, 255, 255, 0.6)',
                                borderRadius: '12px',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(0, 0, 0, 0.05)',
                              }}
                            >
                              <div
                                style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  background: 'rgba(124, 58, 237, 0.1)',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  flexShrink: 0,
                                }}
                              >
                                <item.icon size={16} color="#7c3aed" />
                              </div>
                              <div style={{ flex: 1 }}>
                                <div
                                  style={{
                                    fontSize: '0.9375rem',
                                    fontWeight: 600,
                                    color: '#111827',
                                    marginBottom: '4px',
                                  }}
                                >
                                  {item.title}
                                </div>
                                <div
                                  style={{ fontSize: '0.875rem', color: 'rgba(17, 24, 39, 0.7)', lineHeight: '1.5' }}
                                >
                                  {item.desc}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>

                      {/* Map Interaction Section */}
                      <div
                        style={{
                          background: 'rgba(59, 130, 246, 0.05)',
                          borderRadius: '16px',
                          padding: '20px',
                          border: '1px solid rgba(59, 130, 246, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '12px',
                              background: 'rgba(59, 130, 246, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <MapPin size={20} color="#3b82f6" />
                          </div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                            Map Interaction
                          </h3>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '12px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              fontSize: '0.9375rem',
                              color: '#111827',
                              lineHeight: '1.6',
                            }}
                          >
                            Tap a pin to see cafe details, menu, and prices
                          </div>
                          <div
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '12px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              fontSize: '0.9375rem',
                              color: '#111827',
                              lineHeight: '1.6',
                            }}
                          >
                            <div style={{ marginBottom: '8px', fontWeight: 500 }}>Pin colors show price:</div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div
                                  style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#F44336' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>Red (expensive)</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div
                                  style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#FF9800' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>Orange</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div
                                  style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#FFEB3B' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>Yellow</span>
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <div
                                  style={{ width: '16px', height: '16px', borderRadius: '50%', background: '#4CAF50' }}
                                />
                                <span style={{ fontSize: '0.875rem' }}>Green (cheap)</span>
                              </div>
                            </div>
                          </div>
                          <div
                            style={{
                              padding: '12px',
                              background: 'rgba(255, 255, 255, 0.6)',
                              borderRadius: '12px',
                              backdropFilter: 'blur(10px)',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              fontSize: '0.9375rem',
                              color: '#111827',
                              lineHeight: '1.6',
                            }}
                          >
                            Clustered pins group nearby cafes together. Zoom in to see individual cafe pins.
                          </div>
                        </div>
                      </div>

                      {/* Search Section */}
                      <div
                        style={{
                          background: 'rgba(16, 185, 129, 0.05)',
                          borderRadius: '16px',
                          padding: '20px',
                          border: '1px solid rgba(16, 185, 129, 0.1)',
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                          <div
                            style={{
                              width: '40px',
                              height: '40px',
                              borderRadius: '12px',
                              background: 'rgba(16, 185, 129, 0.15)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0,
                            }}
                          >
                            <Search size={20} color="#10b981" />
                          </div>
                          <h3 style={{ fontSize: '1.125rem', fontWeight: 600, color: '#111827', margin: 0 }}>Search</h3>
                        </div>
                        <div
                          style={{
                            padding: '12px',
                            background: 'rgba(255, 255, 255, 0.6)',
                            borderRadius: '12px',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            fontSize: '0.9375rem',
                            color: '#111827',
                            lineHeight: '1.6',
                          }}
                        >
                          Use the search icon to find cafes by name or browse by category. Search results are ranked by
                          relevance and distance.
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}

        {/* Coffee Search Modal - Already uses portal internally */}
        <CoffeeSearch
          isOpen={showCoffeeSearch}
          onClose={() => setShowCoffeeSearch(false)}
          cafes={
            Object.entries(allCoffeeLocations).map(([name, location]: [string, any]) => ({
              name,
              ...location,
            })) as CoffeeCafe[]
          }
          allCoffeeLocations={allCoffeeLocations}
          userLocation={userLocation ? ([userLocation[0], userLocation[1]] as [number, number]) : null}
          referenceLocation={
            referenceLocation ? ([referenceLocation[0], referenceLocation[1]] as [number, number]) : null
          }
          onSelectCafe={handleSelectCafe}
          onTypeFilter={(typeId) => {
            // Set the coffee type filter when a type is selected from search
            setActiveTypeId(typeId);
            setActiveFilter('coffee');
            setShowCoffeeSearch(false);
          }}
        />

        {/* Coffee List Modal - Portal to document.body for proper z-index */}
        {createPortal(
          <AnimatePresence>
            {showCoffeeList && (
              <motion.div
                initial={{ x: '100%' }}
                animate={{ x: 0 }}
                exit={{ x: '100%' }}
                transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                className="coffee-list-view"
              >
                <div className="coffee-list-header">
                  <button className="coffee-list-back-button" onClick={() => setShowCoffeeList(false)}>
                    <ArrowLeft size={24} />
                  </button>
                  <div className="coffee-list-title-section">
                    <h1 className="coffee-list-title">Coffee Cafes</h1>
                  </div>
                  <div style={{ width: 40 }} />
                </div>
                <div className="coffee-list-sort-filters">
                  <button
                    onClick={() => setListSortBy('distance')}
                    className={`coffee-list-sort-button ${listSortBy === 'distance' ? 'active' : ''}`}
                  >
                    <MapPin size={16} />
                    <span>Location</span>
                  </button>
                  <div style={{ display: 'flex', gap: '0.5rem', flex: 1 }}>
                    <button
                      onClick={() => {
                        setListSortBy('price');
                        if (listSortBy === 'price') {
                          // Toggle price sort order
                          setPriceSortOrder(priceSortOrder === 'lowest' ? 'highest' : 'lowest');
                        }
                      }}
                      className={`coffee-list-sort-button ${listSortBy === 'price' ? 'active' : ''}`}
                      style={{ flex: 1 }}
                    >
                      <DollarSign size={16} />
                      <span>Price</span>
                    </button>
                    <button
                      onClick={() => setListSortBy('rating')}
                      className={`coffee-list-sort-button ${listSortBy === 'rating' ? 'active' : ''}`}
                      style={{ flex: 1 }}
                    >
                      <Star size={16} />
                      <span>Rating</span>
                    </button>
                    {listSortBy === 'price' && ( // Show toggle only when price sorting
                      <button
                        onClick={() => setPriceSortOrder(priceSortOrder === 'lowest' ? 'highest' : 'lowest')}
                        className="coffee-list-price-order-button"
                        title={priceSortOrder === 'lowest' ? 'Show highest prices' : 'Show lowest prices'}
                      >
                        {priceSortOrder === 'lowest' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                      </button>
                    )}
                  </div>
                </div>
                <div className="coffee-list-content">
                  {getSortedCafes.length === 0 ? (
                    <div className="coffee-list-empty">
                      <Coffee size={48} />
                      <p>No cafes found</p>
                      <p className="coffee-list-empty-hint">Try adjusting your filters</p>
                    </div>
                  ) : (
                    <>
                      <div className="coffee-list-results">
                        {displayedItems.map((item, index) => {
                          const { cafe, price, distance, rating } = item;
                          const details: React.ReactNode[] = [];

                          // Add price (if available and activeTypeId is set)
                          if (price !== null && activeTypeId) {
                            details.push(
                              <span key="price" className="coffee-list-item-price">
                                ${price.toFixed(2)}
                              </span>
                            );
                          } else if (price === null && activeTypeId) {
                            details.push(
                              <span
                                key="price"
                                className="coffee-list-item-price"
                                style={{ opacity: 0.5, fontStyle: 'italic' }}
                              >
                                No price
                              </span>
                            );
                          }

                          // Add rating (if available)
                          if (rating !== null && !isNaN(rating)) {
                            details.push(
                              <span
                                key="rating"
                                className="coffee-list-item-rating"
                                style={{ display: 'flex', alignItems: 'center', gap: '2px' }}
                              >
                                {rating.toFixed(1)}
                                <Star size={14} fill="#FFA500" color="#FFA500" style={{ flexShrink: 0 }} />
                              </span>
                            );
                          }

                          // Add distance (if available)
                          if (distance !== null) {
                            details.push(
                              <span key="distance" className="coffee-list-item-distance">
                                {formatDistance(distance)}
                              </span>
                            );
                          }

                          return (
                            <motion.div
                              key={`${cafe.name}-${index}`}
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: Math.min(index * 0.02, 0.3) }}
                              className="coffee-list-item"
                              onClick={() => handleSelectCafe(cafe)}
                            >
                              <div className="coffee-list-item-icon">
                                <Coffee size={24} />
                              </div>
                              <div className="coffee-list-item-content">
                                <h3 className="coffee-list-item-name">{cafe.name}</h3>
                                <div
                                  className="coffee-list-item-details"
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}
                                >
                                  {details.map((detail, i) => (
                                    <React.Fragment key={i}>
                                      {detail}
                                      {i < details.length - 1 && (
                                        <span style={{ color: 'rgba(17, 24, 39, 0.4)', fontSize: '0.75rem' }}>•</span>
                                      )}
                                    </React.Fragment>
                                  ))}
                                </div>
                              </div>
                              <div className="coffee-list-item-arrow">
                                <ArrowLeft size={20} style={{ transform: 'rotate(180deg)' }} />
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>

                      {/* Loading trigger element for infinite scroll */}
                      {displayedCount < getSortedCafes.length && (
                        <div ref={observerTarget} className="coffee-list-loading-trigger">
                          <div className="coffee-list-loading-spinner" />
                        </div>
                      )}

                      {/* End of list indicator */}
                      {displayedCount >= getSortedCafes.length && getSortedCafes.length > 0 && (
                        <div className="coffee-list-end">
                          <p>Showing all {getSortedCafes.length} cafes</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>,
          document.body
        )}
      </div>
    </AppShell>
  );
};

export default CoffeeMapper2;
